import { HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { ChangeDetectorRef, inject, Injectable, Injector, OnDestroy, QueryList, VERSION } from '@angular/core';
import { Params } from '@angular/router';
import { EnvironmentConfiguration } from '@nts/std/environments';
import { ClassConstructor, classToPlain, DEFAULT_METADATA_STORAGE, plainToClass, TypeHelpOptions, TypeMetadata } from '@nts/std/serialization';
import { TelemetryService } from '@nts/std/telemetry';
import { ClassInformationUtility, FileUtils, LocalstorageHelper, LogService, NetworkUtils, OnlineService } from '@nts/std/utility';
import { registerDecorator, ValidationTypes } from 'class-validator';
import { DecoratorUtility } from '@nts/std/utility';
import { BehaviorSubject, EMPTY, firstValueFrom, interval, merge, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, debounceTime, filter, map, share, switchMap, take, takeUntil, takeWhile, tap, timeout } from 'rxjs/operators';
import { RootModelTypeNameInspector } from '../api-clients/decorators/root-model-type-name.decorator';
import { ExternalReaderApiClient } from '../api-clients/external-reader-api-client';
import { AuthService } from '../auth/auth.service';
import { PresentationCache } from '../cache/presentation-cache';
import { GenericsPropertiesTypeInspector, GenericsPropertiesTypeInterface } from '../decorators/generics-properties-type.decorator';
import { IdentityTypeInspector } from '../decorators/identity-type.decorator';
import { RootViewModelTypeInspector } from '../decorators/root-view-model-type.decorator';
import { AttachmentIdentity, BoolDecoratorInterface, BoolValidator, DateDecoratorInterface, DateValidator, EnumDecoratorInterface, EnumValidator, ExternalDecoratorInterface, ExternalValidator, Filter, IgnoreAutomaticDecoratorWarningInspector, InternalDecoratorInterface, InternalValidator, ModelTypeInspector, OperationIdentity, OperationStateResultDto, ReportInfoDto, SpoolProcess, TimeSpanDecoratorInterface, TimeSpanValidator, Where } from '../domain-models';
import { AutoCompleteExternalOptions } from '../domain-models/autocomplete/auto-complete-external-options';
import { BaseValidator } from '../domain-models/decorators/commons/base-validator';
import { NumberDecoratorInterface, NumberValidator } from '../domain-models/decorators/number.decorator';
import { StringDecoratorInterface, StringValidator } from '../domain-models/decorators/string.decorator';
import { GridUserLayoutIdentityDto } from '../domain-models/dto/gird-user-layout.dto';
import { FindValuesOptions } from '../domain-models/find-options/find-values-options';
import { AvailableLayoutDto } from '../domain-models/layout/available-layout-dto';
import { AvailableLayoutsInfoDto } from '../domain-models/layout/available-layouts-info.dto';
import { BaseLayoutDataDto } from '../domain-models/layout/base-layout-data.dto';
import { GridUserLayoutDataDto } from '../domain-models/layout/grid-user-layout-data.dto';
import { LayoutDefinitionIdentity } from '../domain-models/layout/layout-definition.identity';
import { PanelUserLayoutDataDto } from '../domain-models/layout/panel-user-layout-data.dto';
import { CustomFieldMetaData, ExternalLayoutMetaData, GridMetaData, PanelMetaData, SimpleFieldMetaData, SpacerMetaData } from '../layout-meta-data';
import { MetaDataUtils } from '../meta-data/meta-data-utils';
import { TimeSpanMetaData } from '../meta-data/time-span-meta-data';
import { GetByIdentityRequest } from '../requests/get-by-identity-request';
import { CodeValueMessageArg } from '../resources/code-value-message-arg';
import { MessageCodes } from '../resources/message-codes';
import { MessageResourceManager } from '../resources/message-resource-manager';
import { GenericServiceResponse } from '../responses';
import { FindValuesResponse } from '../responses/find-values-response';
import { MetaDataResponse } from '../responses/meta-data-response';
import { ServiceResponse } from '../responses/service-response';
import { UIStarter } from '../starter/ui-starter';
import { ToastMessageService } from './../components/layout/toast-message/toast-message.service';
import { BaseViewModel } from './base-view-model';
import { CollectionViewModel } from './collection-view-model';
import { ColumnInfoCollection } from './column-info-collection';
import { ColumnsUtils } from './columns_utils';
import { CommandFactory } from './commands/command-factory';
import { CommandTypes } from './commands/command-types';
import { UICommandSettingsManager } from './commands/ui-command-settings-manager';
import { ExternalColumnMapInfo } from './external_column_map_info';
import { ModalService } from './modal/modal.service';
import { RootViewModel } from './root-view-model';
import { UnchangedViewModelState } from './states/unchanged-view-model-state';
import { ViewModelState } from './states/view-model-state';
import { ViewModelFactory } from './view-model-factory';
import { ViewModelLocator } from './view-model-locator';
import { BigNumber, DateTime, ExternalFieldInputMode, FieldTypes, FilledButtonType, FilterOperators, MessageButton, MessageResult, MsgClearMode, OperationState, PanelState, SimpleFieldType, SourceMessage, ToastMessageType, ViewModelStates, WhereOperators } from '@nts/std/types';
import { ClassInformationType, DateTimeOffset } from '@nts/std/types';
import type { ActionInProgressInterface, AggregateMetaDataInterface, AttachmentIdentityInterface, AvailableLayoutDtoInterface, BaseErrorInterface, BaseMessageInterface, BaseViewModelInterface, BoolMetaDataInterface, ClassInformationInterface, ColumnInfoCollectionInterface, ColumnInfoInterface, CoreOrchestratorViewModelInterface, CoreToolBarViewModelInterface, DateTimeMetaDataInterface, DomainModelMetaDataInterface, EnumMetaDataInterface, ExternalMetaDataInterface, FindValuesOptionsInterface, FindValuesResponseInterface, GenerateReportParams, GenericServiceResponseInterface, GridUserLayoutDataDtoInterface, InternalCollectionMetaDataInterface, InternalRelationMetaDataInterface, LayoutDefinitionIdentityInterface, LayoutMetaDataInterface, MessageContainerInterface, MessageInViewModelInterface, ModalContainerInterface, NumericMetaDataInterface, OperationIdentityInterface, OperationStateResultDtoInterface, PrintOutDemandDtoInterface, PrintOutInfoDtoInterface, ReportInfoDtoInterface, RootViewModelInterface, ServiceResponseInterface, SpoolProcessInterface, StatusMessageInterface, StringMetaDataInterface, ToastResultInterface, UICommandInterface, UserLayoutMetaDataInterface, ViewModelEventDispatcherInterface, ViewModelInterface, WingViewModelInterface } from '@nts/std/interfaces';
import { MessageContainer } from './message-container';
import type { IdentityInterface, ModelInterface } from '@nts/std/interfaces';
import { ComponentType } from '@angular/cdk/portal';
import { MatDialogConfig } from '@angular/material/dialog';
import { DynamicExternal } from '../domain-models/dynamic-external';
import type { CustomIconInterface } from '@nts/std/interfaces';
import { UserLayoutMetaData } from '../layout-meta-data/user-layout-meta-data';
import { LayoutMetaData } from '../layout-meta-data/layout-meta-data';
import { ViewModelEventDispatcher } from './view-model-event-dispatcher';
import { ModalResult } from './modal/modal-result';
import { PropertyViewModel } from './property-view-model';
import { ExpandableComponent } from '../components/shared/expandable/expandable.component';
import { SimpleFieldComponent } from '../components/controls/core/simple-field/simple-field.component';
import { GridFieldComponent } from '../components/controls/core/grid-field/grid-field.component';
import { ExternalFieldComponent } from '../components/controls/core/external-field/external-field.component';
import { CustomFieldComponent } from '../components/controls/core/custom-field/custom-field.component';
import { CustomFieldContainerComponent } from '../components/controls/custom-field-container/custom-field-container.component';
import { SpacerBoxComponent } from '../components/controls/spacer-box/spacer-box.component';
import { RoutingService } from '../routing/routing.service';
import { DateTimeMetaData } from '../meta-data';
import { DynamicInternal } from '../domain-models/dynamic-internal';
import { BaseError, Information } from '@nts/std/messages';

export const STANDARD_LAYOUT_CODE = 'standard';

export interface HandlePrintOutInfoDtoResponseParams {
  response: GenericServiceResponseInterface<PrintOutInfoDtoInterface>;
  spoolControllerAddress: string,
  reportCommand?: UICommandInterface<any, any, OperationIdentityInterface>,
  onProgress?: (data: { operationState: OperationState, attachments: AttachmentIdentityInterface[], processResult: OperationStateResultDtoInterface; }) => Promise<void>;
  onStop?: () => Promise<void>;
  pollingIntervalMs?: number;
}

export type ReportInfoDtoInterfaceOrGenerateReportParams<TParams, TRequestDto extends PrintOutDemandDtoInterface<TParams>> =
  ReportInfoDtoInterface | GenerateReportParams<TParams, TRequestDto>;

@Injectable()
export abstract class CoreOrchestratorViewModel<
  TRootViewModel extends RootViewModel<TModel, TIdentity>,
  TApiClient extends ExternalReaderApiClient<TModel, TIdentity>,
  TModel extends ModelInterface<TIdentity>,
  TIdentity extends IdentityInterface> extends BaseViewModel implements CoreOrchestratorViewModelInterface<TRootViewModel>, ClassInformationInterface, OnDestroy {

  showPageHeader: boolean = true;
  showToolBar: boolean = true;
  jsonRouteParam: string;
  #routingService = inject(RoutingService);

  classType: ClassInformationType = ClassInformationType.OrchestratorViewModel;

  /**
   * Gestisce la visualizzazione dello stato corrente
   */
  showCurrentState: boolean = true;

  /**
   * Ogni volta che il root view model viene modificato viene lanciato questo evento
   */
  rootViewModelModified: ReplaySubject<void> = new ReplaySubject();

  /**
   * Ogni volta che il root view model viene modificato dal momento della sottoscrizione viene lanciato questo evento
   */
  rootViewModelModifiedFromNow: Subject<void> = new Subject();

  /**
   * Ogni volta che il root view model viene sostituito viene lanciato questo evento
   */
  rootViewModelChanged: ReplaySubject<void> = new ReplaySubject();

  /**
   * Ogni volta che il root view model viene sostituito dal momento della sottoscrizione viene lanciato questo evento
   */
  rootViewModelChangedFromNow: Subject<void> = new Subject();

  /**
   * Ogni volta che lo stato corrente cambia, viene lanciato questo evento
   */
  currentStateChanged: ReplaySubject<void> = new ReplaySubject();

  /**
   * Emette un evento per richiedere il cambio del layout
   */
  changeLayoutRequested: Subject<LayoutDefinitionIdentityInterface> = new Subject<LayoutDefinitionIdentityInterface>();

  /**
   * Emette un evento per richiedere il refresh della pagina dopo il cambio della security
   */
  refreshPageAfterSecurityChange: Subject<void> = new Subject<void>();

  /**
   * Emette un evento per richiedere il reset del layout
   */
  resetLayoutRequested: Subject<LayoutDefinitionIdentityInterface> = new Subject<LayoutDefinitionIdentityInterface>();

  /**
   * Lista dei query sring passati alla maschera
   */
  queryParams: Params;

  /**
   * Lista classi custom aggiunte nel container principale della maschera
   */
  customClasses: string[] = [];

  /**
   * meta data di layout generato partendo dal template
   */
  templateLayoutMetaData: LayoutMetaDataInterface = new LayoutMetaData();

  /**
   * Verificare questa variabile per capire se in questo momento ci sono rebuild in corso del rootViewModel
   */
  rebuildRootViewModelInProgress$ = new BehaviorSubject<boolean>(false);

  domainModel: TModel;
  documentEnabled: boolean = true;
  allViewModelErrorsBuildedList: MessageContainerInterface[] = [];
  allViewModelInformationsBuildedList: MessageContainerInterface[] = [];
  spoolProcessList = [];
  spoolProcessCompleted: Subject<SpoolProcessInterface<any>> = new Subject<SpoolProcessInterface<any>>();
  spoolProcessStarted: Subject<SpoolProcessInterface<any>> = new Subject<SpoolProcessInterface<any>>();
  spoolProcessError: Subject<SpoolProcessInterface<any>> = new Subject<SpoolProcessInterface<any>>();
  moreOptionsMenuItemUpdated: Subject<void> = new Subject();
  mobileMenuItemUpdated: Subject<void> = new Subject();
  openSpoolProcessRequested: Subject<OperationIdentityInterface> = new Subject<OperationIdentityInterface>();
  openLongOpReportRequested: Subject<ReportInfoDtoInterface> = new Subject<ReportInfoDtoInterface>();
  apiClient: TApiClient;

  isDetached$: BehaviorSubject<boolean>;
  askPendingChangesCanceled: Subject<void> = new Subject();
  pendingChangesEnded: Subject<void> = new Subject();
  pendingChangesStarted: Subject<void> = new Subject();
  pendingAutocompleteEnded: Subject<void> = new Subject();
  pendingAutocompleteStarted: Subject<void> = new Subject();
  customIcon$: Subject<CustomIconInterface> = new Subject();
  checkStatusChanged$ = new BehaviorSubject<StatusMessageInterface>(null);

  forceStopRenderPageAfterError: boolean = false;

  private _isModalOpen: boolean = false;

  get isModalOpen(): boolean {
    return this._isModalOpen;
  }
  set isModalOpen(value: boolean) {
    this._isModalOpen = value;
    this.checkStatus().then(status => this.checkStatusChanged$.next(status));
  }

  override get hasErrors(): boolean {
    return this.rootViewModel != null ? this.rootViewModel.hasErrors : false;
  }

  get title(): string {
    return this.getTitle();
  }

  get navigationPanelCollapsed(): boolean {
    return this._navigationPanelCollapsed;
  }

  /**
   * meta data di layout specifica per utente, è definita solo se siamo nel template standard
   */
  get userLayoutMetaData(): UserLayoutMetaDataInterface {
    return this._userLayoutMetaData;
  }

  get isDeactivable(): boolean {
    return this._isDeactivable;
  }

  get externalReturnMode(): boolean {
    return this._externalReturnMode;
  }

  // Ritorna il layoutmetadata corrente indipendentemente se è selezionato uno personalizzato o standard
  get currentLayoutMetaData(): LayoutMetaDataInterface {
    return this.layoutMetaData ? this.layoutMetaData : this.templateLayoutMetaData;
  }

  /**
   * Layout corrente indipententemente se è standard o personalizzato
   */
  get currentLayout(): AvailableLayoutDtoInterface {
    return this._currentLayout;
  }

  get layoutDefaultValueList(): Map<string, string | number | BigNumber | boolean | Date | DateTimeOffset> {
    return this._layoutDefaultValueList;
  }

  get useMessageResourceKey(): boolean {
    return this._useMessageResourceKey;
  }

  get startedAsRelatedClient(): boolean {
    return this._startedAsRelatedClient;
  }

  get toolBarViewModel(): CoreToolBarViewModelInterface {
    return this._toolBarViewModel;
  }

  get wingViewModel(): WingViewModelInterface {
    return this._wingViewModel;
  }

  get viewModel(): BaseViewModelInterface {
    return this._rootViewModel;
  }

  get currentStateDescription(): string {
    return MessageResourceManager.Current.getMessage('std_' + ViewModelStates[this.currentState.value]);
  }

  get customGridColumns(): Map<string, ColumnInfoCollectionInterface> {
    return this._customGridColumns;
  }

  get gridColumns(): Map<string, ColumnInfoCollectionInterface> {
    return this._gridColumns;
  }

  get actionInProgress(): boolean {
    return this._actionInProgress;
  }

  get rootViewModelType(): ClassConstructor<TRootViewModel> {
    return this._rootViewModelType;
  }

  get identityType(): ClassConstructor<TIdentity> {
    return this._identityType;
  }

  get rootViewModel(): TRootViewModel {
    return this._rootViewModel;
  }

  get typedRootViewModel(): TRootViewModel {
    return this._rootViewModel;
  }

  get metadata(): AggregateMetaDataInterface {
    return this._metadata;
  }

  get layoutMetaData(): LayoutMetaDataInterface {
    return this._layoutMetaData;
  }

  get layoutList(): AvailableLayoutDtoInterface[] {
    return this._layoutList;
  }

  get eventDispatcher(): ViewModelEventDispatcherInterface {
    return this._eventDispatcher;
  }
  set eventDispatcher(value: ViewModelEventDispatcherInterface) {
    if (this._eventDispatcher != null) {
      // TODO Tommy: unsubscribe dei vecchi observable (verificare)
      this._eventDispatcher.onActionInProgress.complete();
    }
    this._eventDispatcher = value;
  }

  get currentState(): ViewModelState {
    return this._currentState;
  }
  set currentState(newViewModelState: ViewModelState) {
    const currentState = this._currentState;
    this._currentState = newViewModelState;
    if (!currentState || currentState.value !== newViewModelState.value) {
      this.currentStateChanged.next();
      if (this.rootViewModel?.currentStateChanged) {
        this.rootViewModel?.currentStateChanged.next();
      }
      this.checkStatus().then(status => this.checkStatusChanged$.next(status));
    }
  }

  protected _metadata: AggregateMetaDataInterface;

  /**
   * Metadati per la personalizzazione con pannelli
   */
  protected _layoutMetaData: LayoutMetaDataInterface;

  private _actionInProgress: boolean;
  private _wingViewModel: WingViewModelInterface;
  private _navigationPanelCollapsed: boolean;
  private _userLayoutMetaData: UserLayoutMetaDataInterface;
  private _allViewModelMessagesMap = new Map<string, BaseViewModelInterface>();
  private _toolBarViewModel: CoreToolBarViewModelInterface;
  private _isDeactivable: boolean = undefined;
  private _externalReturnMode: boolean = false;
  private _layoutDefaultValueList: Map<string, string | number | BigNumber | boolean | Date | DateTimeOffset> = new Map<string, string | number | BigNumber | boolean | Date | DateTimeOffset>();
  private _useMessageResourceKey: boolean = true;
  private _startedAsRelatedClient = false;
  private _identityType: ClassConstructor<TIdentity>;
  private _rootViewModelType: ClassConstructor<TRootViewModel>;
  private _rootViewModel: TRootViewModel;
  private _currentState: ViewModelState;
  private _selectedLayoutCode: string = null;
  private _eventDispatcher = new ViewModelEventDispatcher();
  private _layoutList: AvailableLayoutDto[];
  private _currentLayout: AvailableLayoutDto;
  private _pendingChangesSubscription;
  private _pendingChanges = false;
  private _gridColumns: Map<string, ColumnInfoCollectionInterface>;
  private _customGridColumns: Map<string, ColumnInfoCollectionInterface>;
  private _pendingAutocompleteSubscription;
  private _pendingAutocomplete = false;

  constructor(
    apiClient: TApiClient,
    public modalService: ModalService,
    public env: EnvironmentConfiguration,
    public authService: AuthService,
    public toastMessageService: ToastMessageService,
    public onlineService: OnlineService,
  ) {
    super();

    modalService.ovm = this as CoreOrchestratorViewModelInterface;

    this._rootViewModelType = RootViewModelTypeInspector.getValue(this);

    if (this._rootViewModelType === undefined) {
      throw new Error(
        `MetaData ${RootViewModelTypeInspector.META_DATA_KEY} not defined. You must use ${RootViewModelTypeInspector.DECORATOR_NAME}} in ${this.constructor.name}.`
      );
    }

    this._identityType = IdentityTypeInspector.getValue(this);

    if (this._identityType === undefined) {
      throw new Error(
        `MetaData ${IdentityTypeInspector.META_DATA_KEY} not defined. You must use ${IdentityTypeInspector.DECORATOR_NAME} in ${this.constructor.name}.`
      );
    }

    this.apiClient = apiClient;

    const rootModelName = RootModelTypeNameInspector.getValue(this.apiClient);
    const orchestratorViewModelType = ViewModelLocator.getOrchestratorViewModelType(rootModelName);
    const wingViewModelType = ViewModelLocator.getWingViewModelType(orchestratorViewModelType);

    if (wingViewModelType !== undefined) {
      this._wingViewModel = new wingViewModelType(this);
    }

    this.currentState = new UnchangedViewModelState(this);

    this._eventDispatcher.onActionInProgress.subscribe((actionInProgress: boolean | ActionInProgressInterface) => {
      if ((actionInProgress as ActionInProgressInterface).inProgress === true || (actionInProgress as ActionInProgressInterface).inProgress === false) {
        this._actionInProgress = (actionInProgress as ActionInProgressInterface).inProgress;
      } else {
        this._actionInProgress = (actionInProgress as boolean);
      }
    });

    this._eventDispatcher.onAddMessageInViewModel.subscribe((onMessageInViewModel: MessageInViewModelInterface) => {
      this._allViewModelMessagesMap.set(
        onMessageInViewModel.viewModel.uniqueId,
        onMessageInViewModel.viewModel);
      this.rebuildMessageList();
    });

    this._eventDispatcher.onClearMessagesInViewModel.subscribe((viewModel: BaseViewModel) => {
      this._allViewModelMessagesMap.delete(viewModel.uniqueId);
      this.rebuildMessageList();
    });

    this._eventDispatcher.onRemovedMessageInViewModel.subscribe(() => {
      this.rebuildMessageList();
    });

    this._eventDispatcher.onClearAllMessages.subscribe(() => {
      this.removeAllMessages();
      this._allViewModelMessagesMap.clear();
      this.rebuildMessageList();
    });

    this._eventDispatcher.externalModalExecuted.subscribe(() => {
      this.documentEnabled = false;
      this._eventDispatcher.onActionInProgress.next(true);
    });

    this._eventDispatcher.externalModalReturned.subscribe(() => {

      window.focus();
      window.blur();

      this.documentEnabled = true;
      this._eventDispatcher.onActionInProgress.next(false);
    });

    this._eventDispatcher.externalModalReturned.subscribe(() => {

      window.focus();
      window.blur();

      this.documentEnabled = true;
      this._eventDispatcher.onActionInProgress.next(false);
    });

    this._toolBarViewModel = this.getToolBarMenu();
  }

  initWingViewModel(injector: Injector): void {
    const rootModelName: string = RootModelTypeNameInspector.getValue(this.apiClient);
    const orchestratorViewModelType: any = ViewModelLocator.getOrchestratorViewModelType(rootModelName);
    const wingViewModelType: any = ViewModelLocator.getWingViewModelType(orchestratorViewModelType);
    if (wingViewModelType !== undefined) {
      this._wingViewModel = new wingViewModelType(this, injector);
    }
  }

  ngOnDestroy(): void {
    this.destroySubscribers$?.next();

    this._toolBarViewModel?.onDestroy();
    this.isEnabled$?.complete();
    this.destroySubscribers$?.complete();

    // this.destroySubscribers$ = null;
    // this._toolBarViewModel = null;
    // this.isEnabled$ = null;
  }

  abstract getToolBarMenu(): CoreToolBarViewModelInterface;

  override clearErrors() {
    this.removeAllMessages();
  }

  setExternalReturnMode(isExternalReturnMode: boolean): void {
    this._externalReturnMode = isExternalReturnMode;
  }

  handleAdditionalErrors(response: ServiceResponseInterface, additionalErrors: BaseErrorInterface[] = []): void {
    response.errors = [
      // Filtra gli errori a parità di codice e description
      ...new Map(
        [
          ...response.errors,
          ...additionalErrors
        ].map(e => [e.code + e.description, e])
      ).values()
    ];
  }

  updateJsonRouteParam(objectOrIdentity: any = null): void {
    let jsonRouteParam: string = null;
    if (objectOrIdentity) {
      const plainRouteParam: Record<string, any> = classToPlain(objectOrIdentity, { strategy: 'excludeAll' });
      jsonRouteParam = JSON.stringify(plainRouteParam);
    }
    this.jsonRouteParam = jsonRouteParam;
  }

  updateParams(objectOrIdentity: any = null): void {
    this.updateJsonRouteParam(objectOrIdentity);
    const params = new URLSearchParams(this.queryParams);
    UIStarter.updateCurrentRoute(this.metadata.rootFullName, objectOrIdentity ?? undefined, undefined, '?' + params.toString());
  }

  getExternalColumns(entityTypeName: string, externalColumnsMapInfo: ExternalColumnMapInfo[]): Array<ColumnInfoInterface> {
    return ColumnsUtils.getExternalColumns(this.metadata, entityTypeName, externalColumnsMapInfo);
  }

  setStartedAsRelatedClient() {
    this._startedAsRelatedClient = true;
  }

  getCurrentIdentity(): string {
    if (this.domainModel !== undefined) {
      const plain: Record<string, any> = classToPlain(this.domainModel.currentIdentity, { strategy: 'excludeAll' });
      return JSON.stringify(plain);
    } else {
      return undefined;
    }
  }

  /**
   * Scarica un singolo allegato di un processo di spool
   * @param process
   * @param attachmentIdentity
   * @returns
   */
  async downloadSpoolProcessSingleAttachement(
    process: SpoolProcessInterface<any>,
    attachmentIdentity: AttachmentIdentityInterface
  ): Promise<any> {
    return this.apiClient.downloadPrintOutFile(process.spoolControllerAddress, attachmentIdentity).pipe(
      catchError(e => {
        LogService.debug('error!', e);
        return of('');
      })
    ).toPromise().then(
      async (response: HttpResponse<Blob>) => {
        if (response) {
          await this.handleSpoolHttpBlobResponse(
            {
              response,
              process,
              attachmentIdentity
            }
          );
        }
      }
    );
  }

  /**
   * Gestisce il download di un file quando la risposta è di tipo HttpResponse<Blob>
   * @param param0
   * @returns
   */
  protected async handleSpoolHttpBlobResponse({
    response,
    process,
    attachmentIdentity
  }: {
    response: HttpResponse<Blob>,
    process: SpoolProcessInterface<any>,
    attachmentIdentity: AttachmentIdentityInterface;
  }) {
    this.downloadFileFromHttpBlobResponse(response as any);
    return Promise.resolve();
  }

  /**
   * Gestisce il download di un file quando i dati sono in base64
   * @param process
   */
  protected async handleSpoolBase64Data(process: SpoolProcess<any>): Promise<void> {
    const blob: Blob = FileUtils.base64ToBlob(process.dataToPrint, 'application/octet-stream');
    FileUtils.simulateFileDownloadFromBlob(blob, process.fileName);
    return Promise.resolve();
  }

  /**
   * Scarica tutti gli allegati di un processo di spool
   * @param process
   */
  async downloadSpoolProcessAttachements(process: SpoolProcess<any>): Promise<void> {
    const promiseList: any[] = [];
    for (const attachmentIdentity of process.attachments) {
      promiseList.push(
        this.downloadSpoolProcessSingleAttachement(process, attachmentIdentity)
      );

    }
    await Promise.all(promiseList);
  }

  downloadFileFromHttpBlobResponse(httpEvent: HttpEvent<Blob>): boolean {
    /*
    la downladFile viene chiamata due volte per ogni download
    - la prima volta httpEvent è un oggetto semplice non tipizzato
    - la seconda volta httpEvent è di tipo HttpResponse
    - nei casi in cui ci siano errori httpEvent è di tipo ServiceResponse
    */
    let completed: boolean = false;

    if (httpEvent instanceof ServiceResponse) {

      const response: ServiceResponse = httpEvent as ServiceResponse;
      if (!response.operationSuccedeed) {

        let message: string = '';
        response.errors.forEach(e => message += e.description);
        this.toastMessageService.showToast({
          title: MessageResourceManager.Current.getMessage('Error'),
          message: message,
          type: ToastMessageType.error
        });
      }
    } else if (httpEvent instanceof HttpResponse) {
      if (httpEvent && httpEvent.type === HttpEventType.Response) {
        // download file
        const blob: Blob = httpEvent.body;
        let fileName: string = 'filename';
        if (httpEvent.headers.has("content-disposition")) {
          const contentDisposition: string = httpEvent.headers.get("content-disposition");
          // header string ex: "attachment; filename="Contratto di fornitura di servizio BCP vers 5.,pdf"; filename*=UTF-8''Contratto%20di%20fornitura%20di%20servizio%20BCP%20vers%205.%2Cpdf"
          const contentDispositionSplitted: string[] = contentDisposition.split(';');
          const fileNameExtended = contentDispositionSplitted[1].split('filename=');
          // eslint-disable-next-line no-useless-escape
          fileName = fileNameExtended[1].replace(/\"/g, '').trim().split(',').join('');
        }

        FileUtils.simulateFileDownloadFromBlob(blob, fileName);

        completed = true;
      }
    }

    return completed;
  }

  /**
   * Esegue le azioni da compiere al completamento di un processo di spool
   * Se sono presenti allegati li scarica, altrimenti gestisce i dati in base64
   * @param process
   */
  async executeSpoolProcessCompleted(process: SpoolProcess<any>): Promise<void> {
    if (process.downloadAttachements && process.attachments?.length > 0) {
      await this.downloadSpoolProcessAttachements(process);
    } else if (process.fileName && process.dataToPrint) {
      await this.handleSpoolBase64Data(process);
    }
  }

  async openGridSettings(path: string, name: string, dto: GridUserLayoutDataDto): Promise<UserLayoutMetaDataInterface> {

    if (this.actionInProgress == false) {
      this.eventDispatcher.onActionInProgress.next(true);
    }
    let jsonIdentity: string = '';
    let url: string = '';
    let result = null;

    // TODO fino a quando la setGridUserLayoutData non ritorna l'url sono obbligato a chiamare anche la presentation cache
    const userLayoutDataRootFullName = 'LayoutManager.UserLayoutDataObjects.Models.UserLayoutData';
    await PresentationCache.addIfNotExist(userLayoutDataRootFullName);
    url = PresentationCache.get(userLayoutDataRootFullName);

    if (dto) {
      dto.layoutIdentity =
        this.layoutMetaData ? // Se ho un selezionato un template personalizzato
          this.currentLayout.identity :    // Gli passo l'identity del layout
          null;            // Sono nello standard

      const response = await firstValueFrom(this.apiClient.setGridUserLayoutDataAsync(dto));

      if (!response.operationSuccedeed) {
        this.eventDispatcher.onActionInProgress.next(false);
        this.showFromResponse(response, MsgClearMode.ClearAllMessages);
        return result;
      }
    }

    const identity = new GridUserLayoutIdentityDto();
    identity.serviceFullName = this.rootViewModel.aggregateMetaData.rootFullName;
    identity.gridFullPathName = path?.length > 0 ? path + '.' + name : name;

    // Layout custom, devo passare anche il layout identity
    if (this.layoutMetaData) {
      identity.layoutIdentity = this.currentLayout.identity;
    }

    const plainIdentity = classToPlain(identity, { strategy: 'excludeAll' });
    jsonIdentity = JSON.stringify(plainIdentity);
    let modalResponse = new ModalResult<{ operationSuccedeed: boolean, forcePageRefresh: boolean; }>({
      operationSuccedeed: false,
      forcePageRefresh: false
    }, true);

    if (url?.length > 0 && jsonIdentity?.length > 0) {
      this.eventDispatcher.externalModalExecuted.emit();
      modalResponse = await this.showExternalModalWithResultAsync<{ operationSuccedeed: boolean, forcePageRefresh: false; }>(
        url.toLowerCase(),
        jsonIdentity,
        null,
        MessageResourceManager.Current.getMessage('std_GridSettingsWindow_DisplayName')
      );
    } else {
      LogService.warn(MessageResourceManager.Current.getMessage('std_UrlOrJsonIdentityNotValid'));
    }

    if (modalResponse.result?.forcePageRefresh === true) {
      window.location.reload();
      return null;
    }

    const baseLayoutData = new BaseLayoutDataDto();
    if (this.layoutMetaData) {
      baseLayoutData.layoutIdentity = this.currentLayout.identity;
    }

    if (modalResponse.result?.operationSuccedeed === true) {
      const newUserLayoutResponse = await firstValueFrom(this.apiClient.getUserLayoutMetaDataAsync(baseLayoutData));
      if (newUserLayoutResponse.operationSuccedeed && newUserLayoutResponse.result) {
        result = newUserLayoutResponse.result;
      }
    }

    if (url?.length > 0 && jsonIdentity?.length > 0) {
      this.eventDispatcher.externalModalReturned.emit();
    }
    this.eventDispatcher.onActionInProgress.next(false);

    return result;
  }

  async resetLayout(identity: LayoutDefinitionIdentity): Promise<void> {
    this.resetLayoutRequested.next(identity);
  }

  async customizeSecurity(): Promise<void> {
    if (this.actionInProgress == false) {
      this.eventDispatcher.onActionInProgress.next(true);
    }

    const securityRootFullName = 'SecurityManager.SecurityDataObjects.Models.SecurityData';
    await PresentationCache.addIfNotExist(securityRootFullName);
    const url = PresentationCache.get(securityRootFullName);
    if (url?.length > 0) {

      this.eventDispatcher.externalModalExecuted.emit();
      const additionalQueryParams = new URLSearchParams();
      additionalQueryParams.append('modelFullName', this.metadata.rootFullName);
      const result = await this.showExternalModalWithResultAsync<string>(
        url.toLowerCase(),
        null,
        additionalQueryParams,
        MessageResourceManager.Current.getMessage('std_SecurityWindow_DisplayName'),
        false
      );

      if (result.cancel) {
        this.eventDispatcher.externalModalReturned.emit();
      } else {
        this.refreshPageAfterSecurityChange.next();
      }
    }

    this.eventDispatcher.onActionInProgress.next(false);

  }

  async customizeLayouts(): Promise<void> {

    if (this.actionInProgress == false) {
      this.eventDispatcher.onActionInProgress.next(true);
    }
    let jsonIdentity: string = '';
    let url: string = '';

    // Passa il layout corrente

    if (!this.layoutMetaData) {
      // Se ho selezionato lo standard, devo creare un draft
      const responseDraftLayout = await firstValueFrom(this.apiClient.createDraftLayout(this.templateLayoutMetaData));

      if (responseDraftLayout.operationSuccedeed && responseDraftLayout.result?.identity && responseDraftLayout.result?.uiInfo?.fullAddress?.length > 0) {
        const plainIdentity = classToPlain(responseDraftLayout.result?.identity, { strategy: 'excludeAll' });
        jsonIdentity = JSON.stringify(plainIdentity);
        url = responseDraftLayout.result.uiInfo.fullAddress;
      } else {
        this.showFromResponse(responseDraftLayout, MsgClearMode.ClearAllMessages);
      }

    } else {

      const layoutDefinitionRootFullName = 'LayoutManager.LayoutDefinitionObjects.Models.LayoutDefinition';
      await PresentationCache.addIfNotExist(layoutDefinitionRootFullName);
      url = PresentationCache.get(layoutDefinitionRootFullName);
      if (url?.length > 0) {
        const plainIdentity = classToPlain(this.currentLayout.identity, { strategy: 'excludeAll' });
        jsonIdentity = JSON.stringify(plainIdentity);
      }
    }

    if (url?.length > 0 && jsonIdentity?.length > 0) {
      this.eventDispatcher.externalModalExecuted.emit();
      const result: ModalResult<string> = await this.showExternalModalWithResultAsync<string>(
        url.toLowerCase(),
        jsonIdentity,
        null,
        MessageResourceManager.Current.getMessage('std_CustomizeLayoutsWindow_DisplayName')
      );

      if (result.cancel) {
        this.eventDispatcher.externalModalReturned.emit();
      } else {
        const jsonObject = result.result;
        const identity = plainToClass<LayoutDefinitionIdentity, object>(
          LayoutDefinitionIdentity, JSON.parse(jsonObject) as object);

        this.changeLayoutRequested.next(identity);
      }
    } else {
      LogService.warn(MessageResourceManager.Current.getMessage('std_UrlOrJsonIdentityNotValid'));
    }

    this.eventDispatcher.onActionInProgress.next(false);
  }

  async changeLayout(identity: LayoutDefinitionIdentityInterface): Promise<void> {
    this.changeLayoutRequested.next(identity);
  }

  async checkIfPendingChanges(): Promise<boolean> {
    return this.currentState.value === ViewModelStates.Modified ||
      this.currentState.value === ViewModelStates.NewModified;
  }

  async checkStatus(): Promise<StatusMessageInterface> {
    return {
      hasModalOpen: this.isModalOpen,
      pendingChanges: await this.checkIfPendingChanges(),
      version: 1
    };
  }

  /**
   * Visualizza una finestra di conferma all'utente per chiedere se vuole perdere i dati non salvati
   * @returns
   */
  async askConfirmLosingUnsavedDataAsync(): Promise<boolean> {
    const args: Array<CodeValueMessageArg> = [];
    const arg: CodeValueMessageArg = new CodeValueMessageArg();
    arg.code = 'NEWLINE';
    arg.value = '<br>';
    args.push(arg);
    const confirmModalMessage = MessageResourceManager.Current.getMessageWithArgs(MessageCodes.UnsavedData, args);

    const warningMessage = MessageResourceManager.Current.getMessage(MessageCodes.Warning);
    const result = await this.modalService.showMessageAsync(warningMessage, confirmModalMessage, MessageButton.YesNo);

    if (result !== MessageResult.Yes) {
      this.askPendingChangesCanceled.next();
    }
    return result === MessageResult.Yes;
  }

  checkIfCanUnload(): boolean {
    return this.currentState.value !== ViewModelStates.Modified &&
      this.currentState.value !== ViewModelStates.NewModified;
  }

  async confirmLosingUnsavedDataAsync(): Promise<boolean> {
    if (await this.checkIfPendingChanges()) {
      if (this.isModalOpen) {
        return false;
      }

      return this.askConfirmLosingUnsavedDataAsync();
    } else {
      return true;
    }
  }

  getApiClient(): TApiClient {
    return this.apiClient;
  }

  notifyModified() {
    this.rootViewModelModified.next();
    this.rootViewModelModifiedFromNow.next();
    this.currentState.modify();
  }

  async showExternalModalWithResultAsync<TResult>(
    url: string,
    jsonIdentity: string,
    additionalQueryParams = new URLSearchParams(),
    modalTitle?: string,
    externalReturn = true,

    /**
     * Supporto dello stato dei pending changes remoto
     */
    supportRemoteClosingCheck = false,
    customExternalModalViewModel = null,

    /**
     * Supporto dello stato della maschera remoto:
     * - pending changes
     * - has modal open
     */
    supportRemoteStatus = false,
    customModalContainerComponent: ComponentType<ModalContainerInterface> | undefined = undefined,
    config: MatDialogConfig<any> | undefined = undefined
  ): Promise<ModalResult<TResult>> {
    return this.modalService.showExternalModal(
      url,
      jsonIdentity,
      additionalQueryParams,
      modalTitle,
      externalReturn,
      supportRemoteClosingCheck,
      customExternalModalViewModel,
      supportRemoteStatus,
      customModalContainerComponent,
      config
    );
  }

  showFromErrors(errors: Array<BaseErrorInterface>) {
    this.refreshMessages(errors, null, MsgClearMode.NoClear);
  }

  showFromResponse(response: ServiceResponseInterface, clearMode: MsgClearMode) {
    this.refreshMessagesFromResponse(response, clearMode);
  }

  showFromStrings(messages: Array<string>, clearMode: MsgClearMode) {
    this.refreshMessagesFromStrings(messages, clearMode);
  }

  showFromMessages(messages: Array<BaseMessageInterface>, clearMode: MsgClearMode) {
    this.refreshMessagesFromBaseMessages(messages, clearMode);
  }

  removeMessageInViewModel(message: MessageContainerInterface) {
    if (message.uniqueId) {
      const { messageContainerCollection } = this._allViewModelMessagesMap.get(message.uniqueId);
      messageContainerCollection.splice(messageContainerCollection.indexOf(message), 1);
      this.eventDispatcher.onRemovedMessageInViewModel.next(null);
    } else {
      LogService.warn(`Impossibile rimuovere il messaggio, manca l'informazione dell'uniqueId`, message, this._allViewModelMessagesMap);
    }
  }

  focusMessageInViewModel(message: MessageContainer) {
    if (message.uniqueId) {
      const vm = this._allViewModelMessagesMap.get(message.uniqueId) as ViewModelInterface;

      if (vm instanceof PropertyViewModel) {
        const pathWithIndex = vm.propertyPathWithIndex;

        // Se ho delle collection parent, devo preselezionare la riga dove ho l'errore
        this.selectParentCollectionsByPropertyPath(pathWithIndex);
        setTimeout(() => {
          if (vm.isVisible) {
            vm.onFocusRequested.next();
          }
        }, 250);
      } else {
        vm.onFocusRequested.next();
      }
    } else {
      LogService.warn(`Impossibile effettuare il focus sul messaggio, manca l'informazione dell'uniqueId`, message, this._allViewModelMessagesMap);
    }
  }

  protected selectParentCollectionsByPropertyPath(pathWithIndex: string) {

    if (pathWithIndex?.length > 0) {
      const splittedCalculatedPath = pathWithIndex.split('.');

      let baseViewModel = this.rootViewModel;

      for (const key in splittedCalculatedPath) {

        baseViewModel = baseViewModel[splittedCalculatedPath[key]];

        if (baseViewModel.parent instanceof CollectionViewModel) {
          baseViewModel.parent.selection = [baseViewModel];
        }
      }
    }
  }

  /**
   * Imposta il layout di partenza
   * Viene impostato solo se passato nel query string
   *
   * @param layoutCode     codice del layout di partenza
   */
  setStartingLayoutCode(layoutCode: string) {
    this._selectedLayoutCode = layoutCode;
  }

  createMockDomainModel<TEntity extends ModelInterface>(entityClass: ClassConstructor<TEntity>, metadata: AggregateMetaDataInterface): TEntity {
    const newEntity = new entityClass() as ModelInterface;
    this.recursiveMockDomainModel(metadata, metadata.rootMetaData, newEntity);
    return newEntity as TEntity;
  }

  async initialize(): Promise<void> {
    this.eventDispatcher.onInitializing.next();
    this.eventDispatcher.onNavigationPanelCollapsed.pipe(takeUntil(this.destroySubscribers$)).subscribe((isCollpased) => {
      this._navigationPanelCollapsed = isCollpased;
    });
    await this.initValidators();
  }

  async openLongOpReport(reportInfo: ReportInfoDto): Promise<void> {
    this.openLongOpReportRequested.next(reportInfo);
  }

  async initValidators(): Promise<void> {
    const rootDomainModelType = this.apiClient.rootModelType;
    this.recursiveProcessValidators(this.metadata.rootMetaData, rootDomainModelType, [], null);
  }

  async postInitMetaData(metaData: MetaDataResponse): Promise<MetaDataResponse> {
    return metaData;
  }

  async initMetaData(): Promise<MetaDataResponse> {
    const useMessageResourceKey = true;
    let response = new MetaDataResponse();
    if (!this._metadata) {
      response = await firstValueFrom(this.apiClient.getMetaDataAsync(!useMessageResourceKey).pipe(share()));

      this.forceAllPropertiesToExtensionTrueWhenExternalExtensionFound(response);

      this._metadata = response.result;
    } else {
      response.result = this._metadata;
    }
    this._isDeactivable = this._metadata?.isDeactivable;
    this._useMessageResourceKey = useMessageResourceKey;

    if (response.result) {
      response.result.useMessageResourceKey = useMessageResourceKey;
    }

    if (response.operationSuccedeed) {
      this.eventDispatcher.onMetaDataLoaded.next(true);
    }
    return response;
  }

  forceAllPropertiesToExtensionTrueWhenExternalExtensionFound(response: MetaDataResponse): void {
    if (response.operationSuccedeed) {
      // Implement the logic to force all properties to extension true

      for (const domainModel of response.result.domainModels) {
        for (const external of domainModel.externals) {
          if (external.isExtension) {
            this.recursiveForceAllPropertiesToExtensionTrue(external.dependentAggregateMetaData);
          }
        }
      }

      response.result.rootMetaData = response.result.domainModels.find(dm => dm.fullName === response.result.rootMetaData.fullName);
    }
  }

  recursiveForceAllPropertiesToExtensionTrue(domainModelMetaData: AggregateMetaDataInterface): void {
    for (const domainModel of domainModelMetaData.domainModels) {
      for (const property of domainModel.propertyNames) {
        const propertyMetaData = domainModel.getPropertyMetaData(property);
        if (propertyMetaData) {
          propertyMetaData.isExtension = true;
        }
      }
    }

    domainModelMetaData.rootMetaData = domainModelMetaData.domainModels.find(dm => dm.fullName === domainModelMetaData.rootMetaData.fullName);
  }

  canSupportOfflineMode(): Observable<boolean> {
    return of(false);
  }

  /**
   * Inizializza il layout meta data per l'utente corrente
   */
  async initUserLayoutMetaData(rootFullName: string): Promise<void> {

    // Verifico se l'utente ha i permessi per avere il modulo del user layout
    const canAccessToUserLayout = await firstValueFrom(this.canAccessToUserLayout());

    if (canAccessToUserLayout) {
      // Recupero le informazioni dal ms user layout
      const baseLayoutData = new BaseLayoutDataDto();
      const response = await firstValueFrom(this.apiClient.getUserLayoutMetaDataAsync(baseLayoutData));
      if (response.operationSuccedeed && response.result) {
        this._userLayoutMetaData = response.result;
      }
    } else {
      // Recupero le informazioni dal local storage
      this._userLayoutMetaData = await this.readUserLayoutFromLocalStorage(rootFullName);
    }
  }

  private async readUserLayoutFromLocalStorage(rootFullName: string): Promise<UserLayoutMetaDataInterface> {
    const userLayout = await LocalstorageHelper.getStorageItem(await this.getUserLayoutLocalStorageKey());
    if (userLayout) {
      if (userLayout[rootFullName]) {
        return plainToClass<UserLayoutMetaDataInterface, object>(
          UserLayoutMetaData, userLayout[rootFullName] as object);
      }
    }
    return new UserLayoutMetaData();
  }

  private async updateUserLayoutFromLocalStorage(userLayoutMetaData: UserLayoutMetaDataInterface): Promise<void> {
    const userLayout = await LocalstorageHelper.getStorageItem(await this.getUserLayoutLocalStorageKey());
    if (userLayout) {
      userLayout[this.metadata.rootFullName] = classToPlain(userLayoutMetaData, { strategy: 'excludeAll' });
      await LocalstorageHelper.setStorageItem(await this.getUserLayoutLocalStorageKey(), userLayout);
    } else {
      await LocalstorageHelper.setStorageItem(await this.getUserLayoutLocalStorageKey(),
        { [this.metadata.rootFullName]: classToPlain(userLayoutMetaData, { strategy: 'excludeAll' }) }
      );
    }
  }

  private async getUserLayoutLocalStorageKey(): Promise<string> {
    return 'userLayout_' + await this.authService.getUserId() + '_' + await this.authService.getTenantId();
  }

  async setPanelUserLayoutData(dto: PanelUserLayoutDataDto): Promise<void> {

    // Verifico se l'utente ha i permessi per avere il modulo del user layout
    const canAccessToUserLayout: boolean = await firstValueFrom(this.canAccessToUserLayout());

    if (canAccessToUserLayout) {
      const response = await firstValueFrom(this.apiClient.setPanelUserLayoutDataAsync(dto));
      if (!response.operationSuccedeed) {
        LogService.warn('setPanelUserLayoutDataAsync error', response);
      }
    } else {

      // Aggiorno lo userLayoutMetaData
      if (this.userLayoutMetaData) {

        const panelIndex = this.userLayoutMetaData.panels.findIndex((p) => p.panelId === dto.panelMetaData.panelId);
        if (panelIndex > -1) {
          this.userLayoutMetaData.panels[panelIndex] = dto.panelMetaData;
        } else {
          this.userLayoutMetaData.panels.push(dto.panelMetaData);
        }

        // Aggiorno le informazione nel local storage
        await this.updateUserLayoutFromLocalStorage(this.userLayoutMetaData);
      }
    }
  }

  async setGridUserLayoutDataAsync(dto: GridUserLayoutDataDtoInterface): Promise<void> {

    // Verifico se l'utente ha i permessi per avere il modulo del user layout
    const canAccessToUserLayout = await firstValueFrom(this.canAccessToUserLayout());

    if (canAccessToUserLayout) {
      const response = await firstValueFrom(this.apiClient.setGridUserLayoutDataAsync(dto));
      if (!response.operationSuccedeed) {
        LogService.warn('setGridUserLayoutDataAsync error', response);
      }
    } else {

      // Aggiorno lo userLayoutMetaData
      if (this.userLayoutMetaData) {

        const gridIndex = this.userLayoutMetaData.grids.findIndex((g) => g.fullPathName === dto.gridMetaData.fullPathName);
        if (gridIndex > -1) {
          this.userLayoutMetaData.grids[gridIndex] = dto.gridMetaData;
        } else {
          this.userLayoutMetaData.grids.push(dto.gridMetaData);
        }

        // Aggiorno le informazione nel local storage
        this.updateUserLayoutFromLocalStorage(this.userLayoutMetaData);
      }
    }
  }

  /**
   * Resituisce il layout meta data con i pannelli
   * @returns
   */
  async initLayoutsMetaData(): Promise<GenericServiceResponse<AvailableLayoutsInfoDto>> {

    let identity: LayoutDefinitionIdentity = null;

    if (this._selectedLayoutCode?.length > 0 && this._selectedLayoutCode != STANDARD_LAYOUT_CODE) {
      identity = new LayoutDefinitionIdentity();
      identity.code = this._selectedLayoutCode;
    }

    const response = await firstValueFrom(this.apiClient.getAvailableLayoutsAsync(identity));

    const standardLayoutDto = new AvailableLayoutDto();
    standardLayoutDto.displayName = "Standard";
    standardLayoutDto.isPreferred = false;
    standardLayoutDto.identity.code = STANDARD_LAYOUT_CODE;
    this._layoutList = [standardLayoutDto];

    const layoutList = response.operationSuccedeed && response.result?.availableLayouts?.length > 0 ?
      [standardLayoutDto, ...response.result.availableLayouts] : [standardLayoutDto];

    this.initLayoutLogic(layoutList);

    return response;
  }

  initLayoutLogic(availableLayoutDto: AvailableLayoutDto[]) {

    const preferredLayout = availableLayoutDto.filter((l) => l.isPreferred).pop();
    this._layoutList = availableLayoutDto;
    // Imposto il layout corrente
    if (this._selectedLayoutCode) { // Se è stato preselezionato un layout
      if (this._selectedLayoutCode !== 'standard') {
        this._currentLayout = availableLayoutDto.filter((l) => l.identity.code === this._selectedLayoutCode).pop();
        this._layoutMetaData = this.currentLayout?.metaData;
      } else {
        this._currentLayout = availableLayoutDto[0];
      }
    } else { // Se non è stato preselezionato un layout
      this._currentLayout = availableLayoutDto.length === 1 ? availableLayoutDto[0] : preferredLayout;

      this._layoutMetaData = availableLayoutDto.length === 1 ?
        null :        // Layout standard
        preferredLayout?.metaData;  //Seleziono il layout con preferred true altrimenti lo standard
    }

    if (!this._layoutMetaData) {
      // Forzo useMessageResourceKey se il layout selezionato è standard
      this._useMessageResourceKey = false;

    } else {

      // Se uso un layout custom creo layoutDefaultValueList (necessario per i pvm per impostare un valore di default)
      this._layoutDefaultValueList = new Map<string, string | number | BigNumber | boolean | Date | DateTimeOffset>();
      for (const panel of this._layoutMetaData.panels) {

        for (const sf of panel.simpleFields) {
          let currentDefaultValue;

          switch (sf.primitiveType) {
            case SimpleFieldType.BooleanField:
              currentDefaultValue = sf.boolDefaultValue;
              break;
            case SimpleFieldType.DateTimeField:
              currentDefaultValue = sf.dateTimeDefaultValue;
              break;
            case SimpleFieldType.DateTimeOffSetField:
              currentDefaultValue = sf.dateTimeDefaultValue;
              break;
            case SimpleFieldType.EnumField:
              currentDefaultValue = sf.numericDefaultValue != undefined ? sf.numericDefaultValue : sf.stringDefaultValue;
              break;
            case SimpleFieldType.GuidField:
              currentDefaultValue = sf.stringDefaultValue;
              break;
            case SimpleFieldType.NumericField:
              currentDefaultValue = sf.numericDefaultValue;
              break;
            case SimpleFieldType.StringField:
              currentDefaultValue = sf.stringDefaultValue;
              break;
            default:
              currentDefaultValue = sf.stringDefaultValue;
              break;
          }

          if (currentDefaultValue != null) {
            const camelCaseFullPathName = sf.fullPathName.split('.').map((p) => MetaDataUtils.toCamelCase(p)).filter((w) => w !== 'selectedItem').join('.');
            this._layoutDefaultValueList.set(camelCaseFullPathName, currentDefaultValue);
          }
        }

        for (const gm of panel.grids) {
          gm.gridColumns.gridFields.forEach((gc) => {

            const currentDefaultValue = gc.boolDefaultValue != null || gc.stringDefaultValue != null || gc.numericDefaultValue != null;

            if (currentDefaultValue != null) {
              const camelCaseFullPathName = gc.fullPathName.split('.').map((p) => MetaDataUtils.toCamelCase(p)).filter((w) => w !== 'selectedItem').join('.');
              this._layoutDefaultValueList.set(camelCaseFullPathName, currentDefaultValue);
            }
          });
          // gm.gridColumns.externals.forEach((external) => {
          //     external.externalFields.forEach((externalField) => {
          //         if (externalField.defaultValue) {
          //             const camelCaseFullPathName = external.fullPathName.split('.').map((p) => MetaDataUtils.toCamelCase(p)).filter((w) => w !== 'selectedItem').join('.') + '.' + MetaDataUtils.toCamelCase(externalField.name);
          //             this._layoutDefaultValueList.set(camelCaseFullPathName, externalField.defaultValue);
          //         }
          //     })
          // });
        }

        // panel.externals.forEach((ex) => {
        //     const externalPath = ex.path?.length > 0 ? ex.path + '.' : '';
        //     const externalName = ex.name;
        //     ex.externalFields.forEach((ef) => {
        //         if (ef.defaultValue) {
        //             const externalFieldPath = ef.path?.length > 0 ? ef.path + '.' : '';
        //             const externalFieldName = ef.name;
        //             const camelCaseFullPathName = (externalPath + externalName + '.' + externalFieldPath + externalFieldName).split('.').map((p) => MetaDataUtils.toCamelCase(p)).filter((w) => w !== 'selectedItem').join('.');
        //             this._layoutDefaultValueList.set(camelCaseFullPathName, ef.defaultValue);
        //         }
        //     })
        // })
        // TODO mancano gli external sia dalle griglie che dalla root, che dagli internal
      }
    }

    const manager = new UICommandSettingsManager();
    for (const layout of this.layoutList) {
      const layoutCommand = manager.setUICommand(CommandTypes.ChangeLayout,
        CommandFactory.createUICommand(
          async (x) => { await this.changeLayout(layout.identity); },
          () => of(true),
          (x) => {
            return of(
              // layout standard
              (layout?.identity?.code === 'standard' && this._layoutMetaData == null) ||

              // layout selezionato
              (this._selectedLayoutCode === layout?.identity?.code) ||

              // layout preferito e non è stato selezionato alcun layout
              (layout?.identity?.code === preferredLayout?.identity?.code && !this._selectedLayoutCode)
            );
          },
        )
      );
      layoutCommand.displayName = layout.displayName;
      layoutCommand.tooltip = layout.displayName;
      this.toolBarViewModel.customizeFieldsCommandGroup.addCommand(layoutCommand);
    }
  }

  // /**
  //  * Aggiorna i metadati con le informazioni provenienti dal layout passato
  //  */
  // processMetaDataWithLayoutMetaData(
  //   metaData: AggregateMetaData,
  //   layoutMetaData: LayoutMetaData
  // ) {
  //   for (const panel of layoutMetaData.panels) {
  //     for (const field of panel.sortedFields) {
  //       console.log(field.path + '.' + field.name);

  //       if (field instanceof GridMetaData) {
  //         for (const gridField of field.gridColumns.sortedFields) {
  //           console.log(gridField.path + '.' + gridField.name);
  //         }
  //       }
  //     }
  //   }
  // }

  async initPresentationMetaData(): Promise<MetaDataResponse> {
    const useMessageResourceKey = false;

    let response = new MetaDataResponse();
    if (!this._metadata) {
      // response = await firstValueFrom(this.apiClient.getMetaDataAsync(true));
      response = await firstValueFrom(this.apiClient.getPresentationMetaDataAsync(this.currentLayout.identity));

      this.forceAllPropertiesToExtensionTrueWhenExternalExtensionFound(response);

      this._metadata = response.result;

      // Incompleto nel layout personalizzato i campi non visibili non modificati non sono restituiti
      // this.processMetaDataWithLayoutMetaData(response.result, this.layoutMetaData);

    } else {
      response.result = this._metadata;
    }

    this._isDeactivable = this._metadata?.isDeactivable;
    this._useMessageResourceKey = useMessageResourceKey;

    if (response.result) {
      response.result.useMessageResourceKey = false;
    }

    this.eventDispatcher.onMetaDataLoaded.next(true);
    return response;
  }

  async getExternal<TExternalDomainModel extends ModelInterface<TExternalIdentity>, TExternalIdentity extends IdentityInterface>(
    identity: TExternalIdentity, externalDomainModelTypeName: string, externalDomainModelType: ClassConstructor<TExternalDomainModel>): Promise<TExternalDomainModel> {
    const request = new GetByIdentityRequest<TExternalIdentity>();
    request.identity = identity;
    const response = await firstValueFrom(this.apiClient.getExternal<TExternalDomainModel, TExternalIdentity>(
      request, externalDomainModelTypeName, externalDomainModelType));
    // TODO: Gestione Errore ==> response.operationSuccedeed e catch

    this.showFromResponse(response, MsgClearMode.NoClear);

    return response.result;
  }

  createRecursiveDynamicDecoratedProperty(
    domainModelMetaData: DomainModelMetaDataInterface,
    instance: ModelInterface
  ) {
    // TODO mancano le property dinamiche delle internal non dinamiche


    for (const pascalCasePropertyName of domainModelMetaData.propertyNames) {
      const propertyMetaData = domainModelMetaData.getPropertyMetaData(pascalCasePropertyName);
      if (propertyMetaData.isExtension) {
        const camelCasePropertyName = MetaDataUtils.toCamelCase(pascalCasePropertyName);

        Object.defineProperty(instance.extension, camelCasePropertyName, {
          value: null,
          writable: true,
          enumerable: true,
          configurable: true
        });

        // Apply @Expose decorator
        DEFAULT_METADATA_STORAGE.addExposeMetadata({
          target: instance.extension.constructor,
          propertyName: camelCasePropertyName,
          options: {},
        });

        if (propertyMetaData instanceof DateTimeMetaData) {
          // Apply @Transform decorator
          DEFAULT_METADATA_STORAGE.addTransformMetadata({
            target: instance.extension.constructor,
            propertyName: camelCasePropertyName,
            transformFn: propertyMetaData.isDateTimeOffset ? DateTimeOffset.transform : DateTime.transform,
            options: {},
          });

          // // Apply @Type decorator
          // const reflectedType = (Reflect as any).getMetadata('design:type', instance.dynamicData, camelCasePropertyName);
          // DEFAULT_METADATA_STORAGE.addTypeMetadata({
          //     target: instance.extensionData.constructor,
          //     propertyName: camelCasePropertyName,
          //     reflectedType,
          //     typeFunction: propertyMetaData.isDateTimeOffset ? () => DateTimeOffset : () => DateTime,
          //     options: {},
          // });
        }
      }
    }

    for (const internalRelation of domainModelMetaData.internalRelations) {
      if (internalRelation.isExtension) {
        const camelCasePropertyName = MetaDataUtils.toCamelCase(internalRelation.principalPropertyName);

        instance.extension[camelCasePropertyName] = new DynamicInternal(internalRelation.dependentMetaData.name);

        // Apply @Expose decorator
        DEFAULT_METADATA_STORAGE.addExposeMetadata({
          target: instance.extension.constructor,
          propertyName: camelCasePropertyName,
          options: {},
        });

        // Apply @Type decorator
        const reflectedType = (Reflect as any).getMetadata('design:type', instance.extension, camelCasePropertyName);
        DEFAULT_METADATA_STORAGE.addTypeMetadata({
          target: instance.extension.constructor,
          propertyName: camelCasePropertyName,
          reflectedType,
          typeFunction: () => DynamicInternal,
          options: {},
        });

        this.createRecursiveDynamicDecoratedProperty(internalRelation.dependentMetaData, instance.extension[camelCasePropertyName]);

      }
    }

    for (const externalMetaData of domainModelMetaData.externals) {
      if (externalMetaData.isExtension) {
        const camelCasePropertyName = MetaDataUtils.toCamelCase(externalMetaData.principalPropertyName);

        instance.extension[camelCasePropertyName] = new DynamicExternal();

        // Apply @Expose decorator
        DEFAULT_METADATA_STORAGE.addExposeMetadata({
          target: instance.extension.constructor,
          propertyName: camelCasePropertyName,
          options: {},
        });

        // Apply @Type decorator
        const reflectedType = (Reflect as any).getMetadata('design:type', instance.extension, camelCasePropertyName);
        DEFAULT_METADATA_STORAGE.addTypeMetadata({
          target: instance.extension.constructor,
          propertyName: camelCasePropertyName,
          reflectedType,
          typeFunction: () => DynamicExternal,
          options: {},
        });

        this.createRecursiveDynamicDecoratedProperty(externalMetaData.dependentAggregateMetaData.rootMetaData, instance.extension[camelCasePropertyName]);

      }
    }
  }

  async getDynamicExternal<TExternalDomainModel extends ModelInterface<TExternalIdentity>, TExternalIdentity extends IdentityInterface>(
    identity: TExternalIdentity,
    rootMetaData: DomainModelMetaDataInterface
  ): Promise<TExternalDomainModel> {

    const wheres: Where[] = [];

    for (const element of rootMetaData.identityNames) {

      const identityFieldValue = identity[MetaDataUtils.toCamelCase(element)];

      if (identityFieldValue == undefined) {
        LogService.warn(`L'identità passata non contiene il campo ${element} necessario per la ricerca dinamica`);
        return null;
      }

      const filter = new Filter();
      filter.name = element;
      filter.operator = FilterOperators.Equals;
      filter.value = identityFieldValue;

      const where = new Where();
      where.filter = filter;
      where.operator = WhereOperators.Filter;

      wheres.push(where);
    }

    const findOptions = new FindValuesOptions();
    findOptions.take = 1;
    findOptions.skip = 0;
    findOptions.wheres = wheres;
    findOptions.outputProperties = Array.from(
      new Set([...rootMetaData.identityNames, ...rootMetaData.propertyNames].filter((e) => e != null))
    );

    const response = await firstValueFrom(this.apiClient.findValues(
      rootMetaData.name,
      findOptions,
      rootMetaData.fullName,
      this.metadata.rootFullName
    ));

    this.showFromResponse(response, MsgClearMode.NoClear);

    if (response.operationSuccedeed && response.result && response.result.length === 1) {
      const result = new DynamicExternal();
      this.createRecursiveDynamicDecoratedProperty(rootMetaData, result);
      for (const [index, outputProperty] of findOptions.outputProperties.entries()) {
        const fieldName = MetaDataUtils.toCamelCase(outputProperty);
        result.setDynamicPropertyValue(fieldName, response.result[0][index]);
      }
      return result as any as TExternalDomainModel;
    }
    return null;
  }





  getExternalAutoCompleteValues(options: AutoCompleteExternalOptions): Observable<FindValuesResponse> {
    return this.apiClient.autoComplete(options);
  }

  waitForPendingAutocompleteAsync(): Promise<void> {

    return new Promise((resolve) => {
      if (this._pendingAutocomplete) {
        this._pendingAutocompleteSubscription = this.pendingAutocompleteEnded.subscribe(() => {
          this._pendingAutocompleteSubscription.unsubscribe();
          resolve();
        });
      } else {
        resolve();
      }
    });
  }

  waitForPendingChangesAsync(): Promise<void> {

    return new Promise((resolve) => {
      if (this._pendingChanges) {
        this._pendingChangesSubscription = this.pendingChangesEnded.subscribe(() => {
          this._pendingChangesSubscription.unsubscribe();
          resolve();
        });
      } else {
        resolve();
      }
    });
  }

  notifyPendingChangesStarting() {
    this.pendingChangesStarted.next();
    this._pendingChanges = true;
  }

  notifyPendingChangesEnded() {
    this.pendingChangesEnded.next();
    this._pendingChanges = false;
  }

  async about() {
    const response = await firstValueFrom(this.apiClient.getVersionData());
    if (response.operationSuccedeed) {

      const msVersion = MessageResourceManager.Current.getMessageWithStrings('std_AboutModal_MSVersion', response.result.msVersion);
      const msInformativeVersion = MessageResourceManager.Current.getMessageWithStrings('std_AboutModal_MSInformativeVersion', response.result.msInformativeVersion);
      const rootDomainModelName = MessageResourceManager.Current.getMessageWithStrings('std_AboutModal_RootDomainModelName', this.typedRootViewModel.domainModelFullName);
      const msAppUrl = MessageResourceManager.Current.getMessageWithStrings('std_AboutModal_MSAppUrl', this.env.baseAppUrl);

      const jsVersion = MessageResourceManager.Current.getMessageWithStrings('std_AboutModal_JsVersion', response.result.jsVersion);
      const jsInformativeVersion = MessageResourceManager.Current.getMessageWithStrings('std_AboutModal_JsInformativeVersion', response.result.jsInformativeVersion);

      const backendVersion = MessageResourceManager.Current.getMessageWithStrings('std_AboutModal_BackendVersion', response.result.version);
      const backendInformativeVersion = MessageResourceManager.Current.getMessageWithStrings('std_AboutModal_BackendInformativeVersion', response.result.informativeVersion);

      const frameworkJsVersion = MessageResourceManager.Current.getMessageWithStrings('std_AboutModal_FrameworkJsVersion', response.result.frameworkJsVersion);
      const frameworkJsInformativeVersion = MessageResourceManager.Current.getMessageWithStrings('std_AboutModal_FrameworkJsInformativeVersion', response.result.frameworkJsInformativeVersion);

      const frameworkVersion = MessageResourceManager.Current.getMessageWithStrings('std_AboutModal_FrameworkVersion', response.result.frameworkVersion);
      const frameworkInformativeVersion = MessageResourceManager.Current.getMessageWithStrings('std_AboutModal_FrameworkInformativeVersion', response.result.frameworkInformativeVersion);

      const browserLanguage = MessageResourceManager.Current.getMessageWithStrings('std_AboutModal_BrowserLanguage', navigator.language);
      const angularVersion = `Angular: ${VERSION.full}`; // MessageResourceManager.Current.getMessageWithStrings('std_AboutModal_AngularVersion', VERSION.full);

      let workerVersions = '';
      if ('caches' in window) {
        const workerVersionList = (await caches.keys()).filter(key => key.indexOf('assets:app:cache') > -1);
        if (workerVersionList?.length > 0) {
          workerVersions = MessageResourceManager.Current.getMessageWithStrings('std_AboutModal_WorkerVersions', workerVersionList.join(' - '));
        }
      }

      const message =
        msVersion + '<br>' +
        msInformativeVersion + '<br>' +
        rootDomainModelName + '<br>' +
        msAppUrl + '<br>' +

        jsVersion + '<br>' +
        jsInformativeVersion + '<br>' +

        backendVersion + '<br>' +
        backendInformativeVersion + '<br>' +

        frameworkJsVersion + '<br>' +
        frameworkJsInformativeVersion + '<br>' +

        frameworkVersion + '<br>' +
        frameworkInformativeVersion + '<br>' +

        ((workerVersions?.length > 0) ? (workerVersions + '<br>') : '') +

        angularVersion + '<br>' +
        browserLanguage;

      await this.modalService.showMessageAsync({
        title: MessageResourceManager.Current.getMessage(MessageCodes.AboutWindowDisplayName),
        message,
        buttons: MessageButton.Ok,
        width: 'fit-content',
        top: this.#routingService.inIframe ? '8%' : '15%',
      });
    } else {
      this.showFromResponse(response, MsgClearMode.NoClear);
    }
  }

  notifyPendingAutocompleteStarting() {
    this.pendingAutocompleteStarted.next();
    this._pendingAutocomplete = true;
  }

  notifyPendingAutocompleteEnded() {
    this.pendingAutocompleteEnded.next();
    this._pendingAutocomplete = false;
  }

  async startRelatedClient(
    relatedDomainModelFullName: string,
    identity: TIdentity,
    additionalQueryParams: URLSearchParams,
    isRoot: boolean,
    blank = false
  ): Promise<void> {
    // await this.waitForPendingChangesAsync();
    if (!(await firstValueFrom(this.canStartRelatedClient()))) {
      return;
    }
    await PresentationCache.registerRelated(relatedDomainModelFullName);

    const resultBaseUrl = PresentationCache.get(relatedDomainModelFullName);

    if (resultBaseUrl != null && resultBaseUrl !== '') {

      // Non è necessario disabilitare il documento se sto aprendo un nuovo tab
      this.documentEnabled = blank !== false;

      const plainIdentity = classToPlain(identity, { strategy: 'excludeAll' });
      const jsonIdentity = JSON.stringify(plainIdentity);

      UIStarter.startRelatedClient(
        relatedDomainModelFullName,
        resultBaseUrl,
        jsonIdentity,
        additionalQueryParams,
        isRoot,
        blank
      );

    } else {

      // TODO: Notificare che non esiste la gestione del related

    }
  }

  convertJsonIdentity(sourceJsonIdentity: string): TIdentity {

    const identity = new this.identityType();

    const plainJson = JSON.parse(sourceJsonIdentity);

    Object.keys(plainJson).forEach(k => {
      const fieldName = MetaDataUtils.toCamelCase(k);
      identity[fieldName] = plainJson[k];
    });

    return identity;

  }

  canStartRelatedClient(): Observable<boolean> {
    return this.currentStateChanged.pipe(switchMap(() => {
      return this.currentState.canStartRelatedClient();
    }));
  }

  canGenerateReport(): Observable<boolean> {
    return this.currentStateChanged.pipe(switchMap(() => {
      return this.currentState.canStartRelatedClient();
    }));
  }

  private _canAccessToSnapShot = null;

  canAccessToSnapShot(): Observable<boolean> {
    if (this._canAccessToSnapShot) {
      return of(this._canAccessToSnapShot);
    }
    return this.apiClient.userCanAccessToServices([
      'SnapShotService.SnapShotFrameObjects.Models.SnapShotFrame',
      'SnapShotService.SnapShotFrameObjects.Models.SnapShotFrameTenant'
    ]).pipe(
      map((res) => res.operationSuccedeed && res.result.every((v) => v.canAccess === true)),
      catchError(err => {
        LogService.warn(err);
        return of(false);
      }),
      tap((res) => {
        this._canAccessToSnapShot = res;
      })
    );
  }

  private _canAccessToLayout = null;

  canAccessToLayout(): Observable<boolean> {
    if (this._canAccessToLayout) {
      return of(this._canAccessToLayout);
    }
    return this.apiClient.userCanAccessToService('LayoutManager.UserLayoutDataObjects.Models.UserLayoutData').pipe(
      map((res) => res.operationSuccedeed && res.userCanAccessToService),
      catchError(err => {
        LogService.warn(err);
        return of(false);
      }),
      tap((res) => {
        this._canAccessToLayout = res;
      })
    );
  }

  private _canAccessToSecurity = null;

  canAccessToSecurity(): Observable<boolean> {
    if (this._canAccessToSecurity) {
      return of(this._canAccessToSecurity);
    }
    return this.apiClient.userCanAccessToService('SecurityManager.SecurityDataObjects.Models.SecurityData').pipe(
      map((res) => res.operationSuccedeed && res.userCanAccessToService),
      catchError(err => {
        LogService.warn(err);
        return of(false);
      }),
      tap((res) => {
        this._canAccessToSecurity = res;
      })
    );
  }

  private _canAccessToUserLayout = null;

  canAccessToUserLayout(): Observable<boolean> {
    if (this._canAccessToUserLayout) {
      return of(this._canAccessToUserLayout);
    }
    return this.apiClient.userCanAccessToService('LayoutManager.UserLayoutDataObjects.Models.UserLayoutData').pipe(
      map((res) => res.operationSuccedeed && res.userCanAccessToService),
      catchError(err => {
        LogService.warn(err);
        return of(false);
      }),
      tap((res) => {
        this._canAccessToUserLayout = res;
      })
    );
  }

  async startCrudClient<TIdentity extends IdentityInterface>(
    relatedDomainModelFullName: string,
    identity?: TIdentity,
    additionalQueryParams = new URLSearchParams(),
    addHistoryBackButton = false,
    blank = false
  ): Promise<void> {

    await PresentationCache.registerCrudQ(relatedDomainModelFullName);

    const resultBaseUrl = PresentationCache.get(relatedDomainModelFullName);

    if (resultBaseUrl != null && resultBaseUrl !== '') {

      // Non è necessario disabilitare il documento se sto aprendo un nuovo tab
      this.documentEnabled = blank !== false;

      let jsonIdentity = null;

      if (identity) {
        const plainIdentity = classToPlain(identity, { strategy: 'excludeAll' });
        jsonIdentity = JSON.stringify(plainIdentity);
      }

      UIStarter.startCrudClient(
        relatedDomainModelFullName,
        resultBaseUrl,
        jsonIdentity,
        additionalQueryParams,
        addHistoryBackButton,
        blank);

    } else {

      // TODO: Notificare che non esiste la gestione del related

    }
  }

  async startLongOpClient<TObject>(
    domainModelFullName: string,
    object?: TObject,
    additionalQueryParams = new URLSearchParams(),
    addHistoryBackButton = false,
    blank = false
  ): Promise<void> {

    await PresentationCache.registerLongOp(domainModelFullName);

    const resultBaseUrl = PresentationCache.get(domainModelFullName);

    if (resultBaseUrl != null && resultBaseUrl !== '') {

      let jsonObject = null;

      if (object) {
        const plainObject = classToPlain(object, { strategy: 'excludeAll' });
        jsonObject = JSON.stringify(plainObject);
      }

      UIStarter.startLongOpClient(
        domainModelFullName,
        resultBaseUrl,
        jsonObject,
        additionalQueryParams,
        addHistoryBackButton,
        blank
      );

    } else {

      // TODO: Notificare che non esiste la gestione del related

    }
  }

  async startActivityClient<TObject>(
    domainModelFullName: string,
    object?: TObject,
    additionalQueryParams = new URLSearchParams(),
    addHistoryBackButton = false,
    blank = false
  ): Promise<void> {

    await PresentationCache.registerActivity(domainModelFullName);

    const resultBaseUrl = PresentationCache.get(domainModelFullName);

    if (resultBaseUrl != null && resultBaseUrl !== '') {

      let jsonObject = null;

      if (object) {
        const plainObject = classToPlain(object, { strategy: 'excludeAll' });
        jsonObject = JSON.stringify(plainObject);
      }

      UIStarter.startLongOpClient(
        domainModelFullName,
        resultBaseUrl,
        jsonObject,
        additionalQueryParams,
        addHistoryBackButton,
        blank
      );

    } else {
      // TODO: Notificare che non esiste la gestione del related
    }
  }

  addSpoolProcess<TOperationResult>(
    process: SpoolProcess<TOperationResult>,
    pollingIntervalMs: number = 5000
  ) {
    this.spoolProcessList.push(process);
    let stop = false;
    let counter = 0;
    const maxRetry = 50;
    const intervalMs = pollingIntervalMs;

    merge(
      of(0), // Trigger immediately
      interval(intervalMs) // Then continue with regular interval
    ).pipe(
      takeUntil(this.rootViewModelChangedFromNow),
      takeWhile((_) => !stop),
      switchMap(() => process.CheckProcess$.pipe(
        map((data: {
          operationState: OperationState,
          processResult: TOperationResult,
          attachments: AttachmentIdentityInterface[];
        }) => {
          process.processResult = data.processResult;
          process.attachments = data.attachments;
          counter++;

          // Stop after maxRetry * intervalMs Millisecond
          stop = data.operationState !== OperationState.InProgress && data.operationState !== OperationState.WaitingToStart || counter >= maxRetry;
          if (stop && (data.operationState === OperationState.EndedWithError || data.operationState === OperationState.EndedWithSuccess)) {
            this.spoolProcessCompleted.next(process);
          } else if (stop) {

            // Lo spool non è terminato nel tempo limite
            this.spoolProcessError.next(process);
          }
        })
      ))
    ).subscribe();
  }

  async openSpoolProcess(operationIdentity: OperationIdentityInterface) {
    this.openSpoolProcessRequested.next(operationIdentity);
    return Promise.resolve();
  }

  async generateReport<TParams, TRequestDto extends PrintOutDemandDtoInterface<TParams>>(
    reportInfo: GenerateReportParams<TParams, TRequestDto>
  ): Promise<void>;
  /**
   * @deprecated Use the overload with a single parameter of type GenerateReportParams<TParams, TRequestDto>
   */
  async generateReport<TParams, TRequestDto extends PrintOutDemandDtoInterface<TParams>>(
    reportInfo: ReportInfoDtoInterface,
    dtoType: ClassConstructor<TRequestDto>,
    paramsGetter: () => TParams,
    controllerAddress: string,
    command: UICommandInterface<any, any, OperationIdentityInterface>,
    pollingIntervalMs?: number
  ): Promise<void>;
  async generateReport<TParams, TRequestDto extends PrintOutDemandDtoInterface<TParams>>(
    reportInfo: GenerateReportParams<TParams, TRequestDto> | ReportInfoDtoInterface,
    dtoType: ClassConstructor<TRequestDto> = null,
    paramsGetter: () => TParams = null,
    controllerAddress: string = null,
    command: UICommandInterface<any, any, OperationIdentityInterface> = null,
    pollingIntervalMs: number = 5000
  ): Promise<void> {

    if (reportInfo instanceof Object && 'reportInfo' in reportInfo) {
      const {
        reportInfo: extractedReportInfo,
        dtoType: paramDtoType,
        paramsGetter: paramParamsGetter,
        controllerAddress: paramControllerAddress,
        command: paramCommand,
        pollingIntervalMs: paramPollingIntervalMs,
      } = reportInfo;

      // Usa i parametri, scegliendo quelli passati oppure quelli di default
      const finalReportInfo = extractedReportInfo ?? reportInfo;
      const finalDtoType = paramDtoType ?? dtoType;
      const finalParamsGetter = paramParamsGetter ?? paramsGetter;
      const finalControllerAddress = paramControllerAddress ?? controllerAddress;
      const finalCommand = paramCommand ?? command;
      const finalPollingIntervalMs = paramPollingIntervalMs ?? pollingIntervalMs;

      await this.generateReportWithCommand<TParams, TRequestDto>(
        finalCommand,
        finalReportInfo as ReportInfoDtoInterface,
        finalParamsGetter,
        finalControllerAddress,
        finalDtoType,
        finalPollingIntervalMs
      );
      return;
    }

    await this.generateReportWithCommand<TParams, TRequestDto>(
      command,
      reportInfo,
      paramsGetter,
      controllerAddress,
      dtoType,
      pollingIntervalMs
    );
  }

  findValues(
    findOptions: FindValuesOptionsInterface,
    entityToLookUp = '',
    entityToLookUpFullName = ''
  ): Observable<FindValuesResponseInterface> {
    return this.apiClient.findValues(
      entityToLookUp?.length > 0 ? entityToLookUp : this.rootViewModel.domainModelName,
      findOptions,
      entityToLookUpFullName?.length > 0 ? entityToLookUp : this.rootViewModel.domainModelFullName,
      this.rootViewModel.domainModelName,
    );
  }

  protected getTitle(): string {
    if (!this.rootViewModel) {
      return '';
    }

    const domainModel = this.rootViewModel.getDomainModel();
    const identityFieldName = MetaDataUtils.toCamelCase(this.metadata.rootMetaData.identityNames[0]);
    const identiyValue = domainModel.currentIdentity.getPropertyValue(identityFieldName);

    let result = '';

    // la identity è un guid non devo visualizzarla
    if (this.metadata.rootMetaData.guids?.length > 0) {
      result = '';
    } else {
      // Visualizzo l'identity nel titolo se è valorizzata
      if (
        (typeof identiyValue === 'string' && identiyValue.length > 0) ||    // E' una stringa o
        (typeof identiyValue === 'number' && identiyValue > 0)              // E' un numero
      ) {
        const pvm = this.rootViewModel.getProperty(identityFieldName);
        result = pvm.formattedValue;
      }
    }

    return `${this.rootViewModel.metadataShortDescription}${result?.length > 0 ? ' - ' + result : ''}`;
  }

  protected clearMessages(clearMode: MsgClearMode) {
    if (clearMode === MsgClearMode.ClearAllMessages) {
      this.removeAllMessages();
    } else if (clearMode === MsgClearMode.ClearOnlyTemporaryMessage) {
      this.removeApiAndViewModelMessages();
    }
  }

  #changeDetectorRef: ChangeDetectorRef;

  get changeDetectorRef(): ChangeDetectorRef {
    return this.#changeDetectorRef;
  }

  setChangeDetectorRef(changeDetectorRef: ChangeDetectorRef) {
    this.#changeDetectorRef = changeDetectorRef;
  }

  viewModelValidate(clearAllPreviousMessages: boolean): boolean {
    let isValid = true;

    if (this.currentState.canValidate()) {


      // Cancello anzitutto gli errori dalla lista dei messaggi. Il ViewModel.Validate aggiungerà poi tutti
      // gli errori che finiranno direttamente nella lista dei messaggi
      if (clearAllPreviousMessages) {
        this.clearMessages(MsgClearMode.ClearAllMessages);
      }

      this.rootViewModel.validate();

      isValid = this.allViewModelErrorsBuildedList.length === 0;
      this.currentState.validate();
    } else {
      this.notAllowedAction(CommandTypes.Validate);
      isValid = false;
    }

    return isValid;
  }

  protected addInformationMessageIfNotExist(info: BaseMessageInterface) {
    // verifica che non ci sia già una information con lo stesso codice e propertyname
    if (!this.messageContainerCollection.find(x => x.message === info.description)) {
      this.messageContainerCollection.splice(0, 0, MessageContainer.fromBaseMessage(info, this.uniqueId));
      this.eventDispatcher.onAddMessageInViewModel.next({ viewModel: this, messages: [info] });
    }
  }

  protected addErrorMessageIfNotExist(err: BaseErrorInterface) {
    // verifica che non ci sia già un errore con lo stesso codice e propertyname

    if (err?.propertyName?.length > 0) {
      let pvm: BaseViewModelInterface = this.rootViewModel.getProperty(MetaDataUtils.toCamelCase(err?.propertyName));

      if (err?.objectName?.length > 0) {
        const vm = this.findViewModelByDomainModelFullName(err?.objectName);
        if (vm) {
          if (ClassInformationUtility.isCollectionViewModel(vm)) {
            // Se sono in una collection aggiungo gli errori nella sua messageContainerCollection visto che non so in quale riga aggiungerli
            pvm = vm;
          } else if (vm.getProperty != null) {
            pvm = vm.getProperty(MetaDataUtils.toCamelCase(err?.propertyName));

            if (pvm == null) {
              pvm = vm.relationViewModels.get(MetaDataUtils.toCamelCase(err?.propertyName));
            }
          }
        }
      }

      if (pvm) {
        if (!pvm.messageContainerCollection.find(x => x.contains(err))) {
          pvm.addError(SourceMessage.Api, err);
        }
      } else {
        // verifica che non ci sia già un errore con lo stesso codice e propertyname
        if (!this.messageContainerCollection.find(x => x.contains(err))) {
          this.addError(SourceMessage.Api, err);
        }
      }
    } else {
      // verifica che non ci sia già un errore con lo stesso codice e propertyname
      if (!this.messageContainerCollection.find(x => x.contains(err))) {
        this.addError(SourceMessage.Api, err);
      }
    }
  }

  protected findViewModelByDomainModelFullName(objectName: string): ViewModelInterface {

    const path = this.recursiveFindDomainModelFullName(this.rootViewModel.domainModelMetaData, objectName);
    if (path) {
      return path.split('.').reduce((previousValue, currentValue: string) => {
        if (currentValue?.length > 0) {
          return previousValue.relationViewModels.get(currentValue) as ViewModelInterface;
        } else {
          return previousValue;
        }
      }, this.rootViewModel as ViewModelInterface);
    }
    return null;
  }

  protected recursiveFindDomainModelFullName(
    sourceDomainModelMetaData: DomainModelMetaDataInterface,
    domainModelFullName: string,
    path = ''
  ): string {
    if (sourceDomainModelMetaData.fullName == domainModelFullName) {
      return path;
    }
    for (const collection of sourceDomainModelMetaData.internalCollections) {
      const foundPath = this.recursiveFindDomainModelFullName(
        collection.dependentMetaData,
        domainModelFullName,
        path?.length > 0 ?
          (path + '.' + MetaDataUtils.toCamelCase(collection.principalPropertyName)) :
          MetaDataUtils.toCamelCase(collection.principalPropertyName)
      );
      if (foundPath?.length > 0) {
        return foundPath;
      }
    }

    for (const relation of sourceDomainModelMetaData.internalRelations) {
      const foundPath = this.recursiveFindDomainModelFullName(
        relation.dependentMetaData,
        domainModelFullName,
        path?.length > 0 ?
          (path + '.' + MetaDataUtils.toCamelCase(relation.principalPropertyName)) :
          MetaDataUtils.toCamelCase(relation.principalPropertyName)
      );
      if (foundPath?.length > 0) {
        return foundPath;
      }
    }

    for (const external of sourceDomainModelMetaData.externals) {
      const foundPath = this.recursiveFindDomainModelFullName(
        external.dependentAggregateMetaData.rootMetaData,
        domainModelFullName,
        path?.length > 0 ?
          (path + '.' + MetaDataUtils.toCamelCase(external.principalPropertyName)) :
          MetaDataUtils.toCamelCase(external.principalPropertyName)
      );
      if (foundPath?.length > 0) {
        return foundPath;
      }
    }
    return null;
  }

  protected refreshMessagesFromStrings(msgs: Array<string>, clearMode: MsgClearMode) {
    const msgList = new Array<BaseMessageInterface>();
    msgs.forEach(msg => {
      const basemsg = new Information();
      basemsg.description = msg;
      msgList.push(basemsg);
    });
    this.refreshMessagesFromBaseMessages(msgList, clearMode);
  }

  protected refreshMessagesFromBaseMessages(msgs: Array<BaseMessageInterface>, clearMode: MsgClearMode) {
    const errors: BaseErrorInterface[] = [];
    const infos: BaseMessageInterface[] = [];

    msgs.forEach(x => {
      if (x instanceof BaseError) {
        errors.push(x);
      } else if (x instanceof Information) {
        infos.push(x);
      }
    });

    this.refreshMessages(errors, infos, clearMode);
  }

  protected refreshMessagesFromResponse(response: ServiceResponseInterface, clearMode: MsgClearMode) {
    this.refreshMessages(
      response.errors,
      response.informations,
      clearMode
    );
    if (response.errors?.length > 0) {
      this.eventDispatcher.onValidationBarCollapsed.next(false);
    }
    if (response.informations?.length > 0) {
      this.eventDispatcher.onNotificationBarCollapsed.next(false);
    }
  }

  protected refreshMessages(
    errors: Array<BaseErrorInterface>,
    informations: Array<BaseMessageInterface>,
    clearMode: MsgClearMode
  ) {
    if (clearMode === MsgClearMode.ClearAllMessages) {
      this.removeAllMessages();
    } else if (clearMode === MsgClearMode.ClearOnlyTemporaryMessage) {
      this.removeApiAndViewModelMessages();
    }

    if (informations != null) {
      for (const info of informations) {
        this.addInformationMessageIfNotExist(info);
      };
    }
    if (errors != null) {
      for (const err of errors) {
        this.addErrorMessageIfNotExist(err);
      }
    }
  }

  protected removeApiAndViewModelMessages() {
    this.removeMessagesFromCondition(x => {
      return x.sourceMessage === SourceMessage.Api || x.sourceMessage === SourceMessage.ViewModel;
    });
  }

  protected notAllowedAction(cmd: CommandTypes) {
    const message = MessageResourceManager.Current.getMessageWithStrings(MessageCodes.CommandNotAllowed,
      MessageResourceManager.Current.getMessage('std_CMD_' + CommandTypes[cmd]));
    const messages = [message];
    this.refreshMessagesFromStrings(messages, MsgClearMode.ClearOnlyTemporaryMessage);
  }

  protected async tryRebuildViewModelAsync(
    response: ServiceResponseInterface,
    domainModel: TModel,
    fromCreate: boolean,
    clearAllPreviousMessages: boolean) {

    // Se è indicato di cancellare tutti i messaggi li cancello subito tutti, perchè poi i 2 metodi LoadAllExternalList e
    // RefreshMessages chiamati nel seguito non devono cancellare dato che si pesterebbero a vicenda (LoadAllExternalList
    // parte in asincrono
    if (clearAllPreviousMessages) {
      this.removeAllMessages();
    }

    if (domainModel != null) {
      await this.tryRebuildViewModelAsyncWithoutResponse(domainModel, fromCreate);
    }

    // aggiorna la lista messaggi dalla response
    this.refreshMessagesFromResponse(response, MsgClearMode.NoClear);
  }

  protected setEntity(domainModel: TModel): void {
    this.domainModel = domainModel;
  }

  protected async buildGridColumns(viewModel: RootViewModelInterface): Promise<void> {
    if (this._gridColumns === undefined) {
      this._gridColumns = new Map<string, ColumnInfoCollectionInterface>();
      this.buildGridColumnsForDomainModelMetaData(this.metadata.rootMetaData, this.metadata, []);
    }
    if (this._customGridColumns === undefined) {
      this._customGridColumns = new Map<string, ColumnInfoCollectionInterface>();
      await viewModel.initCustomGridColumns();
    }
  }

  async waitRebuildRootViewModel(): Promise<boolean> {
    return firstValueFrom(this.rebuildRootViewModelInProgress$.pipe(filter(_ => this.rootViewModel != null && this.rebuildRootViewModelInProgress$.value === false)));
  }

  async buildTemplateLayoutMetaData(expandableList: QueryList<ExpandableComponent>): Promise<void> {
    this.templateLayoutMetaData = await this.buildLayoutMetaDataFromExpandableComponentList(expandableList);
  }

  private async buildLayoutMetaDataFromExpandableComponentList(expandableList: QueryList<ExpandableComponent>): Promise<LayoutMetaData> {
    const layoutMetaData = new LayoutMetaData();
    if (expandableList?.length > 0) {

      for (const expandable of expandableList) {

        // Abilito le chiamate per la personalizzazione dei pannelli utente solo se è stato impostato un id
        if (expandable.panelId?.length > 0) {

          if (this.userLayoutMetaData?.panels.length > 0) {
            const panelLayoutMetaData = this.userLayoutMetaData?.panels.find((p) => p.panelId === expandable.panelId);
            if (panelLayoutMetaData) {
              expandable.isCollapsed = panelLayoutMetaData.panelState === PanelState.Collapsed;
              expandable.showAdditionalFields = panelLayoutMetaData.panelState === PanelState.Expanded;
            }
          }

          merge(
            expandable.onCollapsed,
            expandable.onExpanded,
            expandable.onExpandedAdditionalFields,
            expandable.onCollapsedAdditionalFields
          ).pipe(
            takeUntil(this.destroySubscribers$),
            debounceTime(1000)
          ).subscribe(

            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            async () => {
              if (this.currentLayout) {
                const dto = new PanelUserLayoutDataDto();

                if (this.currentLayout?.identity?.code != STANDARD_LAYOUT_CODE) {
                  dto.layoutIdentity = this.currentLayout.identity;
                }

                dto.panelMetaData.panelId = expandable.panelId;

                if (expandable.isCollapsed) {
                  dto.panelMetaData.panelState = PanelState.Collapsed;
                } else {
                  dto.panelMetaData.panelState = expandable.showAdditionalFields ? PanelState.Expanded : PanelState.Opened;
                }
                await this.setPanelUserLayoutData(dto);
              }
            }
          );
        }

        const panel = new PanelMetaData();
        if (expandable.isCollapsed) {
          panel.panelState = PanelState.Collapsed;
        } else {
          if (expandable.showAdditionalFields) {
            panel.panelState = PanelState.Expanded;
          } else {
            panel.panelState = PanelState.Opened;
          }
        }

        panel.panelId = expandable.panelId;
        panel.disableToggle = expandable.disableToggle;
        panel.showHeader = expandable.showHeader;

        panel.isVisible = expandable.isVisible;
        panel.promotedFields = expandable.promotedFields.map((pf) => {

          if (pf.propertyPath?.length > 0) {
            return pf.propertyPath + '.' + pf.propertyName;
          }
          return pf.propertyName;
        });

        panel.descriptions.displayName = expandable.title;

        // cicla con il for of per gestire gli await interni passando come parametri anche l'indice
        for (const [index, child] of expandable.expandableChildren.toArray().entries()) {

          if (child instanceof CustomFieldContainerComponent) {
            await firstValueFrom(
              child.customComponentInstance$.pipe(
                takeUntil(this.destroySubscribers$),
                timeout(10000),
                filter(c => c != null),
                take(1)
              ),
              { defaultValue: null }
            );
          }

          switch (child.type) {
            case FieldTypes.Spacer:
              const spacerField = new SpacerMetaData();
              const spacerChild = child as SpacerBoxComponent;
              spacerField.name = spacerChild.name;
              spacerField.position = index + 1;
              spacerField.isAdditionalField = spacerChild.additionalField;
              spacerField.path = spacerChild.path;
              spacerField.isFullColumn = spacerChild.fullColumn;
              spacerField.descriptions.description = spacerChild.description;
              spacerField.descriptions.displayName = spacerChild.displayName;
              spacerField.isVisible = !spacerChild.isHidden;
              spacerField.isDisabled = spacerChild.isDisabled;
              spacerField.uniqueId = spacerChild.uniqueId;
              // spacerField.responsiveConfiguration = spacerChild.responsiveConfiguration;
              panel.spacers.push(spacerField);
              break;

            case FieldTypes.Simple:
              const simpleField = new SimpleFieldMetaData();
              const simpleChild = child as SimpleFieldComponent;
              simpleField.name = simpleChild.name;
              simpleField.position = index + 1;
              simpleField.isAdditionalField = simpleChild.additionalField;
              simpleField.path = simpleChild.path;
              simpleField.isMultiLine = simpleChild.multiline;
              simpleField.isFullColumn = simpleChild.fullColumn;
              simpleField.descriptions.description = simpleChild.description;
              simpleField.descriptions.displayName = simpleChild.displayName;
              simpleField.isVisible = !simpleChild.isHidden;
              simpleField.isDisabled = simpleChild.isDisabled;
              panel.simpleFields.push(simpleField);
              break;

            case FieldTypes.Grid:
              {
                const gridField = new GridMetaData();
                const gridChild = child as GridFieldComponent;

                gridChild.onColumnsChanged.pipe(
                  takeUntil(this.destroySubscribers$),
                  debounceTime(1000)
                ).subscribe(
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  async (dto) => {
                    if (this.currentLayout?.identity?.code != STANDARD_LAYOUT_CODE) {
                      dto.layoutIdentity = this.currentLayout.identity;
                    }
                    await this.setGridUserLayoutDataAsync(dto);
                  }
                );

                gridField.name = gridChild.name;
                gridField.position = index + 1;
                gridField.isAdditionalField = gridChild.additionalField;
                gridField.path = gridChild.path;
                gridField.isFullColumn = gridChild.fullColumn;
                gridField.descriptions.description = gridChild.description;
                gridField.descriptions.displayName = gridChild.displayName;
                gridField.isVisible = !gridChild.isHidden;
                gridField.isDisabled = gridChild.isDisabled;
                gridField.gridColumns = gridChild.columnDefinition;
                gridField.areAllColumnsDisabled = gridChild.isDisabled;
                gridField.areAllCommandsDisabled = gridChild.areAllCommandsDisabled;
                gridField.showTitle = gridChild.showTitle;
                gridField.showToolbar = gridChild.showToolbar;
                gridField.dynamicHeight = gridChild.dynamicHeight;
                gridField.minRowNumberHeight = gridChild.minRowNumberHeight;

                panel.grids.push(gridField);
                break;
              }
            case FieldTypes.External:
              const externalField = new ExternalLayoutMetaData();
              const externalChild = child as ExternalFieldComponent;
              externalField.name = externalChild.name;
              externalField.position = index + 1;
              externalField.isAdditionalField = externalChild.additionalField;
              externalField.path = externalChild.path;
              externalField.isFullColumn = externalChild.fullColumn;
              externalField.availableExternalFields = externalChild.availableExternalFields;
              externalField.searchProperties = externalChild.searchProperties;
              externalField.decodeProperties = externalChild.decodeProperties;
              externalField.fieldSeparator = externalChild.fieldSeparator;
              externalField.descriptions.description = externalChild.description;
              externalField.descriptions.displayName = externalChild.displayName;
              externalField.isVisible = !externalChild.isHidden;
              externalField.isDisabled = externalChild.isDisabled;
              externalField.inputMode = ExternalFieldInputMode.Autocomplete;
              panel.externals.push(externalField);
              break;

            case FieldTypes.ExternalList:
              const externalListField = new ExternalLayoutMetaData();
              const externalLstChild = child as ExternalFieldComponent;
              externalListField.name = externalLstChild.name;
              externalListField.position = index + 1;
              externalListField.isAdditionalField = externalLstChild.additionalField;
              externalListField.path = externalLstChild.path;
              externalListField.isFullColumn = externalLstChild.fullColumn;
              externalListField.availableExternalFields = externalLstChild.availableExternalFields;
              externalListField.searchProperties = externalLstChild.searchProperties;
              externalListField.decodeProperties = externalLstChild.decodeProperties;
              externalListField.fieldSeparator = externalLstChild.fieldSeparator;
              externalListField.descriptions.description = externalLstChild.description;
              externalListField.descriptions.displayName = externalLstChild.displayName;
              externalListField.isVisible = !externalLstChild.isHidden;
              externalListField.isDisabled = externalLstChild.isDisabled;
              externalListField.inputMode = ExternalFieldInputMode.ExternalList;
              panel.externals.push(externalListField);
              break;

            case FieldTypes.Custom:
              const customField = new CustomFieldMetaData();
              const customChild = child as CustomFieldComponent;
              customField.name = customChild.name;
              customField.position = index + 1;
              customField.isAdditionalField = customChild.additionalField;
              customField.path = customChild.path;
              customField.isFullColumn = customChild.fullColumn;
              customField.descriptions.description = customChild.description;
              customField.descriptions.displayName = customChild.displayName;
              customField.isVisible = !customChild.isHidden;
              customField.isDisabled = customChild.isDisabled;
              customField.uniqueId = customChild.uniqueId;
              panel.customFields.push(customField);
              break;
          }
        };

        // TODO tooltip
        panel.descriptions.description = expandable.title;
        layoutMetaData.panels.push(panel);
      };
    }
    return layoutMetaData;
  }

  async getParentIdentityByPathName(path: string): Promise<null | any> {
    await this.waitRebuildRootViewModel();
    const result = path.split('.').reduce((o, j) => {
      if (o != null && j != null && o[MetaDataUtils.toCamelCase(j)] != null) {
        return o[MetaDataUtils.toCamelCase(j)];
      }
      return null;
    }, this.rootViewModel);
    return result;
  }

  protected async tryRebuildViewModelAsyncWithoutResponse(
    domainModel: TModel, fromCreate: boolean, skipPostInit = false
  ) {
    this.rebuildRootViewModelInProgress$.next(true);
    if (domainModel != null) {
      this.setEntity(domainModel);

      // TODO Tommy: map externalist
      // this.externalListMap = new Map<string, ExternalListPVMManagerInterface>();

      const viewModel: TRootViewModel = await ViewModelFactory.createRootViewModel<TRootViewModel, TModel, TIdentity>(
        this._rootViewModelType, this.domainModel, this.metadata, this, skipPostInit, this.apiClient.rootModelType
      ) as TRootViewModel;

      await this.buildGridColumns(viewModel);

      // destroy old viewmodel (viene effettuato ricorsivamente prima sui suoi childviewmodel)
      if (this.rootViewModel) {
        this.rootViewModel.onDestroy();
      }
      this.setRootViewModel(viewModel as TRootViewModel);

      // imposta il title della window
      // TODO Tommy: recuperare dalle risorse il nome dell'applicazione
      window.document.title = `${this.title} - ${this.env.appTitle}`;

      await this.executeDefaultTrackPageView();

      // TODO Tommy: loadAllExternalList
      // this.loadAllExternalList(fromCreate);
    }
    this.rebuildRootViewModelInProgress$.next(false);
  }

  async localReplicaAutocomplete<T>(model: T): Promise<T> {
    return null;
  }

  protected async executeDefaultTrackPageView(): Promise<void> {
    TelemetryService.trackPageView();
  }

  private rebuildMessageList() {
    this.allViewModelErrorsBuildedList = [];
    for (const [_, { messageContainerCollection: value }] of this._allViewModelMessagesMap) {
      this.allViewModelErrorsBuildedList.push(...value.filter((v) => v.isErrorMessage));
    }
    this.allViewModelInformationsBuildedList = [];
    for (const [_, { messageContainerCollection: value }] of this._allViewModelMessagesMap) {
      this.allViewModelInformationsBuildedList.push(...value.filter((v) => !v.isErrorMessage));
    }
  }

  setRootViewModel(value: TRootViewModel) {
    // Pulisce il dictionary da tutti messaggi (errori e informazioni) in tutti i vm;
    this._eventDispatcher.onClearAllMessages.next();
    this._rootViewModel?.onDestroy();
    this._rootViewModel = value;
    // TODO Tommy gestire isMock/isVirtual
    // if (this.entity.isMock === false) {
    this.rootViewModelChanged?.next();
    this.rootViewModelChangedFromNow?.next();
    // }
  }

  private recursiveProcessValidators(
    currentDomainModelMetaData: DomainModelMetaDataInterface,
    currentDomainModelType: ClassConstructor<any>,
    processedDomainModels = [],
    parentDomainModelMetaData: DomainModelMetaDataInterface
  ) {

    if (processedDomainModels.find((element) => element === currentDomainModelMetaData.fullName) === undefined) {
      processedDomainModels.push(currentDomainModelMetaData.fullName);

      // #region Stringhe
      currentDomainModelMetaData.strings?.forEach((stringMetaData: StringMetaDataInterface) => {
        const propertyName: string = MetaDataUtils.toCamelCase(stringMetaData.name);

        let isRequired: boolean = stringMetaData.isRequired;

        // Non più necessaria nel client, i backing field devono sempre essere con required uguale a false indipendentemente da quello che dice il server
        // @deprecated Nel caso in cui il campo corrente è un backing field di un external
        // @deprecated Il required viene definito dall'external
        // @deprecated if (MetaDataUtils.checkIfPropertyIsACodeForExternal(currentDomainModelMetaData, stringMetaData.name)) {
        // @deprecated    const externalMetaData = currentDomainModelMetaData.externals.find(
        // @deprecated        (external: ExternalMetaData) => external.associationProperties && external.associationProperties.length > 0 && external.associationProperties.find(ass => ass.principalPropertyName === stringMetaData.name));
        // @deprecated    isRequired = externalMetaData.isRequired;
        // @deprecated }

        // è un backing field di un external?
        if (MetaDataUtils.checkIfPropertyIsACodeForExternal(currentDomainModelMetaData, stringMetaData.name)) {
          // forzo il required a false
          isRequired = false;
        }

        // Nel caso in cui il campo corrente è un campo di associazione di un internal relation
        // Il required è sempre false
        if (parentDomainModelMetaData && MetaDataUtils.checkIfPropertyIsAnInternalAssociationCode(parentDomainModelMetaData, currentDomainModelMetaData, stringMetaData.name)) {
          isRequired = false;
        }

        // Nel caso in cui il campo corrente è un campo di associazione di un internal collection
        // Il required è sempre false
        if (parentDomainModelMetaData && MetaDataUtils.checkIfPropertyIsAnInternalCollectionAssociationCode(parentDomainModelMetaData, currentDomainModelMetaData, stringMetaData.name)) {
          isRequired = false;
        }

        // Verifica se è stato utilizzato un decoratore
        const decoratorData: StringMetaDataInterface = BaseValidator.getPropertyMetaData(currentDomainModelType, propertyName) as StringMetaDataInterface;

        // Sovrascrivo con il required indicato nel decoratore
        if (decoratorData?.isRequired != null) {
          isRequired = decoratorData.isRequired;
        }

        // Mergio le informazione dai meta-dati con quelli (se esiste) del decoratore
        const decoratorInterface: StringDecoratorInterface = {
          context: decoratorData?.context,
          displayNameKey: decoratorData?.descriptions.displayNameKey ?? stringMetaData.descriptions.displayNameKey,
          displayName: decoratorData?.descriptions.displayName ?? stringMetaData.descriptions.displayName,
          descriptionKey: decoratorData?.descriptions.descriptionKey ?? stringMetaData.descriptions.descriptionKey,
          description: decoratorData?.descriptions.description ?? stringMetaData.descriptions.description,
          shortNameKey: decoratorData?.descriptions.shortNameKey ?? stringMetaData.descriptions.shortNameKey,
          shortName: decoratorData?.descriptions.shortName ?? stringMetaData.descriptions.shortName,
          maxLength: decoratorData?.maxLen ?? stringMetaData.maxLen,
          isRequired,
          isEmail: decoratorData?.isEmail ?? stringMetaData.isEmail,
          isMainDescription: decoratorData?.isMainDescription ?? stringMetaData.isMainDescription,
          allowedChars: decoratorData?.allowedCharacters ?? stringMetaData.allowedCharacters,
        } as StringDecoratorInterface;

        // Rimuovo il decoratore per il required se esisteva
        if (decoratorData?.isRequired) {
          DecoratorUtility.removePropertyDecorator(currentDomainModelType, propertyName, ValidationTypes.IS_DEFINED);
        }

        // Rimuovo il vecchio decoratore se esisteva
        if (decoratorData) {
          DecoratorUtility.removePropertyDecorator(currentDomainModelType, propertyName, ValidationTypes.CUSTOM_VALIDATION);
        }

        // Metodo di base per tutti i decoratori
        BaseValidator.initBaseValidatorWithConstructor(decoratorInterface, currentDomainModelType, propertyName);

        // Registro il decoratore
        registerDecorator({
          target: currentDomainModelType,
          propertyName,
          options: { context: decoratorInterface.context },
          constraints: [decoratorInterface],
          validator: StringValidator
        });

        // Aggiunge informazioni alla property sulla validazione sul tipo della classe
        StringValidator.buildPropertyMetaData<StringDecoratorInterface>(
          currentDomainModelType,
          propertyName,
          decoratorInterface
        );
      });
      // #endregion Stringhe

      // #region TimeSpan
      currentDomainModelMetaData.timeSpans?.forEach((timeSpanMetaData: TimeSpanMetaData) => {
        const propertyName: string = MetaDataUtils.toCamelCase(timeSpanMetaData.name);

        // Verifica se è stato utilizzato un decoratore
        const decoratorData: TimeSpanMetaData = BaseValidator.getPropertyMetaData(currentDomainModelType, propertyName) as TimeSpanMetaData;

        // Mergio le informazione dai meta-dati con quelli (se esiste) del decoratore
        const decoratorInterface: TimeSpanDecoratorInterface = {
          context: decoratorData?.context,
          displayNameKey: decoratorData?.descriptions.displayNameKey ?? timeSpanMetaData.descriptions.displayNameKey,
          displayName: decoratorData?.descriptions.displayName ?? timeSpanMetaData.descriptions.displayName,
          descriptionKey: decoratorData?.descriptions.descriptionKey ?? timeSpanMetaData.descriptions.descriptionKey,
          description: decoratorData?.descriptions.description ?? timeSpanMetaData.descriptions.description,
          shortNameKey: decoratorData?.descriptions.shortNameKey ?? timeSpanMetaData.descriptions.shortNameKey,
          shortName: decoratorData?.descriptions.shortName ?? timeSpanMetaData.descriptions.shortName,
          isRequired: decoratorData?.isRequired ?? timeSpanMetaData.isRequired,
          isAutoComputed: decoratorData?.isAutoComputed ?? timeSpanMetaData.isAutoComputed,
          minValue: decoratorData?.minValue ?? timeSpanMetaData.minValue,
          maxValue: decoratorData?.maxValue ?? timeSpanMetaData.maxValue
        } as TimeSpanDecoratorInterface;

        // Rimuovo il decoratore per il required se esisteva
        if (decoratorData?.isRequired) {
          DecoratorUtility.removePropertyDecorator(currentDomainModelType, propertyName, ValidationTypes.IS_DEFINED);
        }

        // Rimuovo il vecchio decoratore se esisteva
        if (decoratorData) {
          DecoratorUtility.removePropertyDecorator(currentDomainModelType, propertyName, ValidationTypes.CUSTOM_VALIDATION);
        }

        // Metodo di base per tutti i decoratori
        BaseValidator.initBaseValidatorWithConstructor(decoratorInterface, currentDomainModelType, propertyName);

        // Registro il decoratore
        registerDecorator({
          target: currentDomainModelType,
          propertyName,
          options: { context: decoratorInterface.context },
          constraints: [decoratorInterface],
          validator: TimeSpanValidator
        });

        // Aggiunge informazioni alla property sulla validazione sul tipo della classe
        TimeSpanValidator.buildPropertyMetaData<TimeSpanDecoratorInterface>(
          currentDomainModelType,
          propertyName,
          decoratorInterface
        );
      });
      // #endregion TimeSpan

      // #region DateTime
      currentDomainModelMetaData.dateTimes?.forEach((dateTimeMetaData: DateTimeMetaDataInterface) => {
        const propertyName: string = MetaDataUtils.toCamelCase(dateTimeMetaData.name);

        // Verifica se è stato utilizzato un decoratore
        const decoratorData: DateTimeMetaDataInterface = BaseValidator.getPropertyMetaData(currentDomainModelType, propertyName) as DateTimeMetaDataInterface;

        // Mergio le informazione dai meta-dati con quelli (se esiste) del decoratore
        const decoratorInterface = {
          context: decoratorData?.context,
          displayNameKey: decoratorData?.descriptions.displayNameKey ?? dateTimeMetaData.descriptions.displayNameKey,
          displayName: decoratorData?.descriptions.displayName ?? dateTimeMetaData.descriptions.displayName,
          descriptionKey: decoratorData?.descriptions.descriptionKey ?? dateTimeMetaData.descriptions.descriptionKey,
          description: decoratorData?.descriptions.description ?? dateTimeMetaData.descriptions.description,
          shortNameKey: decoratorData?.descriptions.shortNameKey ?? dateTimeMetaData.descriptions.shortNameKey,
          shortName: decoratorData?.descriptions.shortName ?? dateTimeMetaData.descriptions.shortName,
          isRequired: decoratorData?.isRequired ?? dateTimeMetaData.isRequired,
          compareMode: decoratorData?.compareMode ?? dateTimeMetaData.compareMode,
          openDirection: decoratorData?.openDirection ?? dateTimeMetaData.openDirection,
          comparePropertyName: decoratorData?.comparePropertyName ?? dateTimeMetaData.comparePropertyName,
          isDateTimeOffset: decoratorData?.isDateTimeOffset ?? dateTimeMetaData.isDateTimeOffset,
          minValue: decoratorData?.minValue ?? dateTimeMetaData.minValue,
          maxValue: decoratorData?.maxValue ?? dateTimeMetaData.maxValue,
        } as DateDecoratorInterface;

        // Rimuovo il decoratore per il required se esisteva
        if (decoratorData?.isRequired) {
          DecoratorUtility.removePropertyDecorator(currentDomainModelType, propertyName, ValidationTypes.IS_DEFINED);
        }

        // Rimuovo il vecchio decoratore se esisteva
        if (decoratorData) {
          DecoratorUtility.removePropertyDecorator(currentDomainModelType, propertyName, ValidationTypes.CUSTOM_VALIDATION);
        }

        // Metodo di base per tutti i decoratori
        BaseValidator.initBaseValidatorWithConstructor(decoratorInterface, currentDomainModelType, propertyName);

        // Registro il decoratore
        registerDecorator({
          target: currentDomainModelType,
          propertyName,
          options: { context: decoratorInterface.context },
          constraints: [decoratorInterface],
          validator: DateValidator
        });

        // Aggiunge informazioni alla property sulla validazione sul tipo della classe
        DateValidator.buildPropertyMetaData<DateDecoratorInterface>(
          currentDomainModelType,
          propertyName,
          decoratorInterface
        );
      });
      // #endregion DateTime

      // #region Numerics
      currentDomainModelMetaData.numerics?.forEach((numericMetaData: NumericMetaDataInterface) => {
        const propertyName: string = MetaDataUtils.toCamelCase(numericMetaData.name);

        let isRequired: boolean = numericMetaData.isRequired;

        // Non più necessaria nel client, i backing field devono sempre essere con required uguale a false indipendentemente da quello che dice il server
        // @deprecated Nel caso in cui il campo corrente è un backing field di un external
        // @deprecated Il required viene definito dall'external
        // @deprecated if (MetaDataUtils.checkIfPropertyIsACodeForExternal(currentDomainModelMetaData, numericMetaData.name)) {
        // @deprecated    const externalMetaData: ExternalMetaData = currentDomainModelMetaData.externals.find(
        // @deprecated        (external: ExternalMetaData) => external.associationProperties && external.associationProperties.length > 0 && external.associationProperties.find(ass => ass.principalPropertyName === numericMetaData.name));
        // @deprecated    isRequired = externalMetaData.isRequired;
        // @deprecated }

        // è un backing field di un external?
        if (MetaDataUtils.checkIfPropertyIsACodeForExternal(currentDomainModelMetaData, numericMetaData.name)) {
          // forzo il required a false
          isRequired = false;
        }

        // Nel caso in cui il campo corrente è un campo di associazione di un internal relation
        // Il required è sempre false
        if (parentDomainModelMetaData && MetaDataUtils.checkIfPropertyIsAnInternalAssociationCode(parentDomainModelMetaData, currentDomainModelMetaData, numericMetaData.name)) {
          isRequired = false;
        }

        // Nel caso in cui il campo corrente è un campo di associazione di un internal collection
        // Il required è sempre false
        if (parentDomainModelMetaData && MetaDataUtils.checkIfPropertyIsAnInternalCollectionAssociationCode(parentDomainModelMetaData, currentDomainModelMetaData, numericMetaData.name)) {
          isRequired = false;
        }

        // Verifica se è stato utilizzato un decoratore
        const decoratorData: NumericMetaDataInterface = BaseValidator.getPropertyMetaData(currentDomainModelType, propertyName) as NumericMetaDataInterface;

        // Sovrascrivo con il required indicato nel decoratore
        if (decoratorData?.isRequired != null) {
          isRequired = decoratorData.isRequired;
        }

        // Mergio le informazione dai meta-dati con quelli (se esiste) del decoratore
        const decoratorInterface = {
          context: decoratorData?.context,
          displayNameKey: decoratorData?.descriptions.displayNameKey ?? numericMetaData.descriptions.displayNameKey,
          displayName: decoratorData?.descriptions.displayName ?? numericMetaData.descriptions.displayName,
          descriptionKey: decoratorData?.descriptions.descriptionKey ?? numericMetaData.descriptions.descriptionKey,
          description: decoratorData?.descriptions.description ?? numericMetaData.descriptions.description,
          shortNameKey: decoratorData?.descriptions.shortNameKey ?? numericMetaData.descriptions.shortNameKey,
          shortName: decoratorData?.descriptions.shortName ?? numericMetaData.descriptions.shortName,
          maxValue: decoratorData?.maxValue ?? numericMetaData.maxValue,
          minValue: decoratorData?.minValue ?? numericMetaData.minValue,
          maxDecimalPrecision: decoratorData?.maxDecimalPrecision ?? numericMetaData.maxDecimalPrecision,
          maxIntegerPrecision: decoratorData?.maxIntegerPrecision ?? numericMetaData.maxIntegerPrecision,
          isDecimal: decoratorData?.isDecimal ?? numericMetaData.isDecimal,
          isRequired,
        } as NumberDecoratorInterface;

        // Rimuovo il decoratore per il required se esisteva
        if (decoratorData?.isRequired) {
          DecoratorUtility.removePropertyDecorator(currentDomainModelType, propertyName, ValidationTypes.IS_DEFINED);
        }

        // Rimuovo il vecchio decoratore se esisteva
        if (decoratorData) {
          DecoratorUtility.removePropertyDecorator(currentDomainModelType, propertyName, ValidationTypes.CUSTOM_VALIDATION);
        }

        // Metodo di base per tutti i decoratori
        BaseValidator.initBaseValidatorWithConstructor(decoratorInterface, currentDomainModelType, propertyName);

        // Registro il decoratore
        registerDecorator({
          target: currentDomainModelType,
          propertyName,
          options: { context: decoratorInterface.context },
          constraints: [decoratorInterface],
          validator: NumberValidator
        });

        // Aggiunge informazioni alla property sulla validazione sul tipo della classe
        NumberValidator.buildPropertyMetaData<NumberDecoratorInterface>(
          currentDomainModelType,
          propertyName,
          decoratorInterface
        );
      });
      // #endregion Numerics

      // #region Bools
      currentDomainModelMetaData.bools?.forEach((boolMetaData: BoolMetaDataInterface) => {
        const propertyName: string = MetaDataUtils.toCamelCase(boolMetaData.name);

        // Verifica se è stato utilizzato un decoratore
        const decoratorData: BoolMetaDataInterface = BaseValidator.getPropertyMetaData(currentDomainModelType, propertyName) as BoolMetaDataInterface;

        // Mergio le informazione dai meta-dati con quelli (se esiste) del decoratore
        const decoratorInterface = {
          context: decoratorData?.context,
          isRequired: decoratorData?.isRequired ?? boolMetaData.isRequired,
          displayNameKey: decoratorData?.descriptions.displayNameKey ?? boolMetaData.descriptions.displayNameKey,
          displayName: decoratorData?.descriptions.displayName ?? boolMetaData.descriptions.displayName,
          descriptionKey: decoratorData?.descriptions.descriptionKey ?? boolMetaData.descriptions.descriptionKey,
          description: decoratorData?.descriptions.description ?? boolMetaData.descriptions.description,
          shortNameKey: decoratorData?.descriptions.shortNameKey ?? boolMetaData.descriptions.shortNameKey,
          shortName: decoratorData?.descriptions.shortNameKey ?? boolMetaData.descriptions.shortName,
        } as BoolDecoratorInterface;

        // Rimuovo il decoratore per il required se esisteva
        if (decoratorData?.isRequired) {
          DecoratorUtility.removePropertyDecorator(currentDomainModelType, propertyName, ValidationTypes.IS_DEFINED);
        }

        // Rimuovo il vecchio decoratore se esisteva
        if (decoratorData) {
          DecoratorUtility.removePropertyDecorator(currentDomainModelType, propertyName, ValidationTypes.CUSTOM_VALIDATION);
        }

        // Metodo di base per tutti i decoratori
        BaseValidator.initBaseValidatorWithConstructor(decoratorInterface, currentDomainModelType, propertyName);

        // Registro il decoratore
        registerDecorator({
          target: currentDomainModelType,
          propertyName,
          options: { context: decoratorInterface.context },
          constraints: [decoratorInterface],
          validator: BoolValidator
        });

        // Aggiunge informazioni alla property sulla validazione sul tipo della classe
        BoolValidator.buildPropertyMetaData<BoolDecoratorInterface>(
          currentDomainModelType,
          propertyName,
          decoratorInterface
        );
      });
      // #endregion Bools

      // #region Enums
      currentDomainModelMetaData.enums?.forEach((enumMetaData: EnumMetaDataInterface) => {
        const propertyName: string = MetaDataUtils.toCamelCase(enumMetaData.name);

        // Verifica se è stato utilizzato un decoratore
        const decoratorData: EnumMetaDataInterface = BaseValidator.getPropertyMetaData(currentDomainModelType, propertyName) as EnumMetaDataInterface;

        // Recurpero le informazioni dai meta-dati
        const decoratorInterface: EnumDecoratorInterface = {
          context: decoratorData?.context,
          displayNameKey: decoratorData?.descriptions.displayNameKey ?? enumMetaData.descriptions.displayNameKey,
          displayName: decoratorData?.descriptions.displayName ?? enumMetaData.descriptions.displayName,
          descriptionKey: decoratorData?.descriptions.descriptionKey ?? enumMetaData.descriptions.descriptionKey,
          description: decoratorData?.descriptions.description ?? enumMetaData.descriptions.description,
          shortNameKey: decoratorData?.descriptions.shortNameKey ?? enumMetaData.descriptions.shortNameKey,
          shortName: decoratorData?.descriptions.shortName ?? enumMetaData.descriptions.shortName,
          isRequired: decoratorData?.isRequired ?? enumMetaData.isRequired,
        } as EnumDecoratorInterface;

        // Rimuovo il decoratore per il required se esisteva
        if (decoratorData?.isRequired) {
          DecoratorUtility.removePropertyDecorator(currentDomainModelType, propertyName, ValidationTypes.IS_DEFINED);
        }

        // Rimuovo il vecchio decoratore se esisteva
        if (decoratorData) {
          DecoratorUtility.removePropertyDecorator(currentDomainModelType, propertyName, ValidationTypes.CUSTOM_VALIDATION);
        }

        // Metodo di base per tutti i decoratori
        BaseValidator.initBaseValidatorWithConstructor(decoratorInterface, currentDomainModelType, propertyName);

        // Registro il decoratore
        registerDecorator({
          target: currentDomainModelType,
          propertyName,
          options: { context: decoratorInterface.context },
          constraints: [decoratorInterface],
          validator: EnumValidator
        });

        // Aggiunge informazioni alla property sulla validazione sul tipo della classe
        EnumValidator.buildPropertyMetaData<EnumDecoratorInterface>(
          currentDomainModelType,
          propertyName,
          decoratorInterface
        );
      });
      // #endregion Enums

      // #region Externals
      currentDomainModelMetaData.externals?.forEach((externalMetaData: ExternalMetaDataInterface) => {
        const propertyName: string = MetaDataUtils.toCamelCase(externalMetaData.principalPropertyName);

        // Verifica se è stato utilizzato un decoratore
        const decoratorData: ExternalMetaDataInterface = BaseValidator.getPropertyMetaData(currentDomainModelType, propertyName) as ExternalMetaDataInterface;

        // Mergio le informazione dai meta-dati con quelli (se esiste) del decoratore
        const decoratorInterface = {
          context: decoratorData?.context,
          displayNameKey: decoratorData?.descriptions.displayNameKey ?? externalMetaData.descriptions.displayNameKey,
          displayName: decoratorData?.descriptions.displayName ?? externalMetaData.descriptions.displayName,
          descriptionKey: decoratorData?.descriptions.descriptionKey ?? externalMetaData.descriptions.descriptionKey,
          description: decoratorData?.descriptions.description ?? externalMetaData.descriptions.description,
          shortNameKey: decoratorData?.descriptions.shortNameKey ?? externalMetaData.descriptions.shortNameKey,
          shortName: decoratorData?.descriptions.shortName ?? externalMetaData.descriptions.shortName,
          isRequired: decoratorData?.isRequired ?? externalMetaData.isRequired,
          isRemote: decoratorData?.isRemote ?? externalMetaData.isRemote,
          parentIdentityPropertyPathName: decoratorData?.parentIdentityPropertyPathName ?? externalMetaData.parentIdentityPropertyPathName,
          principalPName1: decoratorData?.associationProperties[0]?.principalPropertyName ?? externalMetaData?.associationProperties[0]?.principalPropertyName,
          dependantPName1: decoratorData?.associationProperties[0]?.dependentPropertyName ?? externalMetaData?.associationProperties[0]?.dependentPropertyName,
          principalPName2: decoratorData?.associationProperties[1]?.principalPropertyName ?? externalMetaData?.associationProperties[1]?.principalPropertyName,
          dependantPName2: decoratorData?.associationProperties[1]?.dependentPropertyName ?? externalMetaData?.associationProperties[1]?.dependentPropertyName,
          principalPName3: decoratorData?.associationProperties[2]?.principalPropertyName ?? externalMetaData?.associationProperties[2]?.principalPropertyName,
          dependantPName3: decoratorData?.associationProperties[2]?.dependentPropertyName ?? externalMetaData?.associationProperties[2]?.dependentPropertyName,
          principalPName4: decoratorData?.associationProperties[3]?.principalPropertyName ?? externalMetaData?.associationProperties[3]?.principalPropertyName,
          dependantPName4: decoratorData?.associationProperties[3]?.dependentPropertyName ?? externalMetaData?.associationProperties[3]?.dependentPropertyName,
        } as ExternalDecoratorInterface;

        // Rimuovo il decoratore per il required se esisteva
        if (decoratorData?.isRequired) {
          DecoratorUtility.removePropertyDecorator(currentDomainModelType, propertyName, ValidationTypes.IS_DEFINED);
        }

        // Rimuovo il vecchio decoratore se esisteva
        if (decoratorData) {
          DecoratorUtility.removePropertyDecorator(currentDomainModelType, propertyName, ValidationTypes.CUSTOM_VALIDATION);
        }

        // Metodo di base per tutti i decoratori
        BaseValidator.initBaseValidatorWithConstructor(decoratorInterface, currentDomainModelType, propertyName);

        // Registro il decoratore
        registerDecorator({
          target: currentDomainModelType,
          propertyName,
          options: { context: decoratorInterface.context },
          constraints: [decoratorInterface],
          validator: ExternalValidator
        });

        // Aggiunge informazioni alla property sulla validazione sul tipo della classe
        ExternalValidator.buildPropertyMetaData<ExternalDecoratorInterface>(
          currentDomainModelType,
          propertyName,
          decoratorInterface
        );

        // recupero il tipo dal decoratore @Type
        const typeMetadata: TypeMetadata = DEFAULT_METADATA_STORAGE.findTypeMetadata(currentDomainModelType, propertyName);
        if (typeMetadata && typeMetadata.typeFunction({
          newObject: new currentDomainModelType()
        } as any)) {
          this.recursiveProcessValidators(
            externalMetaData.dependentAggregateMetaData.rootMetaData,
            typeMetadata.typeFunction({
              newObject: new currentDomainModelType()
            } as TypeHelpOptions) as ClassConstructor<any>,
            processedDomainModels,
            currentDomainModelMetaData
          );
        } else {
          LogService.warn(`ATTENZIONE: decoratori automatici non gestiti per la classe ${externalMetaData.dependentAggregateMetaData.rootMetaData.name} associata alla property ${externalMetaData.principalPropertyName}`);
        }
      });
      // #endregion Externals

      // #region Internal
      currentDomainModelMetaData.internalRelations?.forEach((internalRelationMetaData: InternalRelationMetaDataInterface) => {
        const propertyName: string = MetaDataUtils.toCamelCase(internalRelationMetaData.principalPropertyName);

        // Verifica se è stato utilizzato un decoratore
        const decoratorData: InternalRelationMetaDataInterface = BaseValidator.getPropertyMetaData(currentDomainModelType, propertyName) as InternalRelationMetaDataInterface;

        // Mergio le informazione dai meta-dati con quelli (se esiste) del decoratore
        const decoratorInterface = {
          context: decoratorData?.context,
          displayNameKey: decoratorData?.descriptions.displayNameKey ?? internalRelationMetaData.descriptions.displayNameKey,
          displayName: decoratorData?.descriptions.displayName ?? internalRelationMetaData.descriptions.displayName,
          descriptionKey: decoratorData?.descriptions.descriptionKey ?? internalRelationMetaData.descriptions.descriptionKey,
          description: decoratorData?.descriptions.description ?? internalRelationMetaData.descriptions.description,
          shortNameKey: decoratorData?.descriptions.shortNameKey ?? internalRelationMetaData.descriptions.shortNameKey,
          shortName: decoratorData?.descriptions.shortName ?? internalRelationMetaData.descriptions.shortName,
          principalPName1: decoratorData?.associationProperties[0]?.principalPropertyName ?? internalRelationMetaData?.associationProperties[0]?.principalPropertyName,
          dependantPName1: decoratorData?.associationProperties[0]?.dependentPropertyName ?? internalRelationMetaData?.associationProperties[0]?.dependentPropertyName,
          principalPName2: decoratorData?.associationProperties[1]?.principalPropertyName ?? internalRelationMetaData?.associationProperties[1]?.principalPropertyName,
          dependantPName2: decoratorData?.associationProperties[1]?.dependentPropertyName ?? internalRelationMetaData?.associationProperties[1]?.dependentPropertyName,
          principalPName3: decoratorData?.associationProperties[2]?.principalPropertyName ?? internalRelationMetaData?.associationProperties[2]?.principalPropertyName,
          dependantPName3: decoratorData?.associationProperties[2]?.dependentPropertyName ?? internalRelationMetaData?.associationProperties[2]?.dependentPropertyName,
          principalPName4: decoratorData?.associationProperties[3]?.principalPropertyName ?? internalRelationMetaData?.associationProperties[3]?.principalPropertyName,
          dependantPName4: decoratorData?.associationProperties[3]?.dependentPropertyName ?? internalRelationMetaData?.associationProperties[3]?.dependentPropertyName,
        } as InternalDecoratorInterface;

        // Rimuovo il vecchio decoratore se esisteva
        if (decoratorData) {
          DecoratorUtility.removePropertyDecorator(currentDomainModelType, propertyName, ValidationTypes.CUSTOM_VALIDATION);
        }

        // Metodo di base per tutti i decoratori
        BaseValidator.initBaseValidatorWithConstructor(decoratorInterface, currentDomainModelType, propertyName);

        // Registro il decoratore
        registerDecorator({
          target: currentDomainModelType,
          propertyName,
          options: { context: decoratorInterface.context },
          constraints: [decoratorInterface],
          validator: InternalValidator
        });

        // Aggiunge informazioni alla property sulla validazione sul tipo della classe
        InternalValidator.buildPropertyMetaData<InternalDecoratorInterface>(
          currentDomainModelType, propertyName, decoratorInterface);

        // recupero il tipo dal decoratore @Type
        const typeMetadata: TypeMetadata = DEFAULT_METADATA_STORAGE.findTypeMetadata(currentDomainModelType, propertyName);
        if (typeMetadata && typeMetadata.typeFunction({
          newObject: new currentDomainModelType()
        } as any)) {
          this.recursiveProcessValidators(
            internalRelationMetaData.dependentMetaData,
            typeMetadata.typeFunction({
              newObject: new currentDomainModelType()
            } as TypeHelpOptions) as ClassConstructor<any>,
            processedDomainModels,
            currentDomainModelMetaData
          );
        } else if (GenericsPropertiesTypeInspector.isApplied(new currentDomainModelType)) {

          const camelCaseProperty = MetaDataUtils.toCamelCase(internalRelationMetaData.principalPropertyName);
          const innerCurrentDomainModelType = ((new currentDomainModelType) as GenericsPropertiesTypeInterface).getGenericPropertyType(camelCaseProperty);
          if (innerCurrentDomainModelType) {
            this.recursiveProcessValidators(
              internalRelationMetaData.dependentMetaData,
              innerCurrentDomainModelType,
              processedDomainModels,
              currentDomainModelMetaData
            );
          } else {

            let ignoreObject = null;
            if (IgnoreAutomaticDecoratorWarningInspector.isApplied(new currentDomainModelType)) {
              ignoreObject = IgnoreAutomaticDecoratorWarningInspector.getValue(new currentDomainModelType);
            }

            if (
              ignoreObject == null ||
              (ignoreObject[internalRelationMetaData.principalPropertyName] == null && ignoreObject[MetaDataUtils.toCamelCase(internalRelationMetaData.principalPropertyName)] == null) ||
              (ignoreObject[internalRelationMetaData.principalPropertyName] != internalRelationMetaData.dependentMetaData.name && ignoreObject[internalRelationMetaData.principalPropertyName] != null) ||
              (ignoreObject[MetaDataUtils.toCamelCase(internalRelationMetaData.principalPropertyName)] != internalRelationMetaData.dependentMetaData.name && ignoreObject[MetaDataUtils.toCamelCase(internalRelationMetaData.principalPropertyName)] != null)
            ) {
              LogService.warn(`[1<->1] ATTENZIONE: decoratori automatici non gestiti per la classe ${internalRelationMetaData.dependentMetaData.name} associata alla property ${internalRelationMetaData.principalPropertyName} della classe ${internalRelationMetaData.principalMetaData.name}`);
              LogService.warn(`Prova ad aggiungere questo chiave nel decoratore ${GenericsPropertiesTypeInspector.DECORATOR_NAME} nella classe ${internalRelationMetaData.principalMetaData.name}:
    ${MetaDataUtils.toCamelCase(internalRelationMetaData.principalPropertyName)}: ${internalRelationMetaData.dependentMetaData.name}`);
            }
          }
        } else {

          let ignoreObject = null;
          if (IgnoreAutomaticDecoratorWarningInspector.isApplied(new currentDomainModelType)) {
            ignoreObject = IgnoreAutomaticDecoratorWarningInspector.getValue(new currentDomainModelType);
          }

          if (
            ignoreObject == null ||
            (ignoreObject[internalRelationMetaData.principalPropertyName] == null && ignoreObject[MetaDataUtils.toCamelCase(internalRelationMetaData.principalPropertyName)] == null) ||
            (ignoreObject[internalRelationMetaData.principalPropertyName] != internalRelationMetaData.dependentMetaData.name && ignoreObject[internalRelationMetaData.principalPropertyName] != null) ||
            (ignoreObject[MetaDataUtils.toCamelCase(internalRelationMetaData.principalPropertyName)] != internalRelationMetaData.dependentMetaData.name && ignoreObject[MetaDataUtils.toCamelCase(internalRelationMetaData.principalPropertyName)] != null)
          ) {
            LogService.warn(`[1<->1] ATTENZIONE: decoratori automatici non gestiti per la classe ${internalRelationMetaData.dependentMetaData.name} associata alla property ${internalRelationMetaData.principalPropertyName} della classe ${internalRelationMetaData.principalMetaData.name}`);
            LogService.warn(`Prova ad aggiungere questo decoratore nella classe ${internalRelationMetaData.principalMetaData.name}:
    ${GenericsPropertiesTypeInspector.DECORATOR_NAME}({
        ${MetaDataUtils.toCamelCase(internalRelationMetaData.principalPropertyName)}: ${internalRelationMetaData.dependentMetaData.name}
    })`);
          }
        }
      });
      // #endregion Internal

      // #region Internal collection
      currentDomainModelMetaData.internalCollections?.forEach((internalCollectionMetaData: InternalCollectionMetaDataInterface) => {

        const propertyName: string = MetaDataUtils.toCamelCase(internalCollectionMetaData.principalPropertyName);

        // Verifica se è stato utilizzato un decoratore
        const decoratorData: InternalCollectionMetaDataInterface = BaseValidator.getPropertyMetaData(currentDomainModelType, propertyName) as InternalCollectionMetaDataInterface;

        // Mergio le informazione dai meta-dati con quelli (se esiste) del decoratore
        const decoratorInterface = {
          displayNameKey: decoratorData?.descriptions.displayNameKey ?? internalCollectionMetaData.descriptions.displayNameKey,
          displayName: decoratorData?.descriptions.displayName ?? internalCollectionMetaData.descriptions.displayName,
          descriptionKey: decoratorData?.descriptions.descriptionKey ?? internalCollectionMetaData.descriptions.descriptionKey,
          description: decoratorData?.descriptions.description ?? internalCollectionMetaData.descriptions.description,
          shortNameKey: decoratorData?.descriptions.shortNameKey ?? internalCollectionMetaData.descriptions.shortNameKey,
          shortName: decoratorData?.descriptions.shortName ?? internalCollectionMetaData.descriptions.shortName,
          principalPName1: decoratorData?.associationProperties[0]?.principalPropertyName ?? internalCollectionMetaData?.associationProperties[0]?.principalPropertyName,
          dependantPName1: decoratorData?.associationProperties[0]?.dependentPropertyName ?? internalCollectionMetaData?.associationProperties[0]?.dependentPropertyName,
          principalPName2: decoratorData?.associationProperties[1]?.principalPropertyName ?? internalCollectionMetaData?.associationProperties[1]?.principalPropertyName,
          dependantPName2: decoratorData?.associationProperties[1]?.dependentPropertyName ?? internalCollectionMetaData?.associationProperties[1]?.dependentPropertyName,
          principalPName3: decoratorData?.associationProperties[2]?.principalPropertyName ?? internalCollectionMetaData?.associationProperties[2]?.principalPropertyName,
          dependantPName3: decoratorData?.associationProperties[2]?.dependentPropertyName ?? internalCollectionMetaData?.associationProperties[2]?.dependentPropertyName,
          principalPName4: decoratorData?.associationProperties[3]?.principalPropertyName ?? internalCollectionMetaData?.associationProperties[3]?.principalPropertyName,
          dependantPName4: decoratorData?.associationProperties[3]?.dependentPropertyName ?? internalCollectionMetaData?.associationProperties[3]?.dependentPropertyName,
          isUniqueFunc: decoratorData?.isUniqueFunc ?? internalCollectionMetaData?.isUniqueFunc,
          uniqueFields: decoratorData?.uniqueFields ?? internalCollectionMetaData?.uniqueFields,
          context: decoratorData?.context,
          isCollection: true
        } as InternalDecoratorInterface;

        // Rimuovo il vecchio decoratore se esisteva
        if (decoratorData) {
          DecoratorUtility.removePropertyDecorator(currentDomainModelType, propertyName, ValidationTypes.CUSTOM_VALIDATION);
        }

        // Metodo di base per tutti i decoratori
        BaseValidator.initBaseValidatorWithConstructor(decoratorInterface, currentDomainModelType, propertyName);

        // Registro il decoratore
        registerDecorator({
          target: currentDomainModelType,
          propertyName,
          options: { context: decoratorData?.context },
          constraints: [decoratorInterface],
          validator: InternalValidator
        });

        // Aggiunge informazioni alla property sulla validazione sul tipo della classe
        InternalValidator.buildPropertyMetaData<InternalDecoratorInterface>(
          currentDomainModelType, propertyName, decoratorInterface);

        // recupero il tipo dal decoratore @Type
        const typeMetadata: TypeMetadata = DEFAULT_METADATA_STORAGE.findTypeMetadata(currentDomainModelType, propertyName);

        if (typeMetadata && typeMetadata.typeFunction({
          newObject: new currentDomainModelType()
        } as any)) {

          const collectionDomainModelType = typeMetadata.typeFunction({
            newObject: new currentDomainModelType()
          } as any) as any;
          // recupero il tipo dell'elemento dal tipo della collection
          const collectionElementDomainModelType = ModelTypeInspector.getValue(new collectionDomainModelType());
          if (collectionElementDomainModelType) {
            this.recursiveProcessValidators(
              internalCollectionMetaData.dependentMetaData,
              collectionElementDomainModelType,
              processedDomainModels,
              currentDomainModelMetaData
            );
          } else {
            LogService.warn(`[1->N] ATTENZIONE: decoratori automatici non gestiti per la classe ${internalCollectionMetaData.dependentMetaData.name} associata alla property ${internalCollectionMetaData.principalPropertyName} della classe ${internalCollectionMetaData.principalMetaData.name}`);
            LogService.warn(`Prova ad aggiungere questo decoratore alla classe ${collectionDomainModelType.prototype.constructor.name}: ${ModelTypeInspector.DECORATOR_NAME}(${internalCollectionMetaData.dependentMetaData.name})`);
          }
        } else if (GenericsPropertiesTypeInspector.isApplied(new currentDomainModelType)) {

          const camelCaseProperty = MetaDataUtils.toCamelCase(internalCollectionMetaData.principalPropertyName);
          const collectionDomainModelType = ((new currentDomainModelType) as GenericsPropertiesTypeInterface).getGenericPropertyType(camelCaseProperty);

          if (collectionDomainModelType) {

            // recupero il tipo dell'elemento dal tipo della collection
            const collectionElementDomainModelType = ModelTypeInspector.getValue(new collectionDomainModelType());
            if (collectionElementDomainModelType) {
              this.recursiveProcessValidators(
                internalCollectionMetaData.dependentMetaData,
                collectionElementDomainModelType,
                processedDomainModels,
                currentDomainModelMetaData
              );
            } else {
              LogService.warn(`[1->N] ATTENZIONE: decoratori automatici non gestiti per la classe ${internalCollectionMetaData.dependentMetaData.name} associata alla property ${camelCaseProperty} della classe ${internalCollectionMetaData.principalMetaData.name}`);
              LogService.warn(`Prova ad aggiungere questo decoratore alla classe ${collectionDomainModelType.prototype.constructor.name}: ${ModelTypeInspector.DECORATOR_NAME}(${internalCollectionMetaData.dependentMetaData.name})`);
            }
          } else {
            LogService.warn(`[1->N] ATTENZIONE: decoratori automatici non gestiti per la classe ${internalCollectionMetaData.dependentMetaData.name} associata alla property ${camelCaseProperty} della classe ${internalCollectionMetaData.principalMetaData.name}`);
            LogService.warn(`Prova ad aggiungere questo chiave nel decoratore ${GenericsPropertiesTypeInspector.DECORATOR_NAME} nella classe ${internalCollectionMetaData.principalMetaData.name}:
${MetaDataUtils.toCamelCase(internalCollectionMetaData.principalPropertyName)}: ${internalCollectionMetaData.dependentMetaData.name}`);
          }
        } else {
          const camelCaseProperty = MetaDataUtils.toCamelCase(internalCollectionMetaData.principalPropertyName);

          LogService.warn(`[1->N] ATTENZIONE: decoratori automatici non gestiti per la classe ${internalCollectionMetaData.dependentMetaData.name} associata alla property ${camelCaseProperty} della classe ${internalCollectionMetaData.principalMetaData.name}`);
          LogService.warn(`Prova ad aggiungere questo decoratore nella classe ${internalCollectionMetaData.principalMetaData.name}:
${GenericsPropertiesTypeInspector.DECORATOR_NAME}({
    ${MetaDataUtils.toCamelCase(camelCaseProperty)}: ${internalCollectionMetaData.dependentMetaData.name}
})`);
        }
      });
      // #endregion Internal collection
    }
  }

  // TODO Tommy da revisionare e testare
  private recursiveMockDomainModel(
    metadata: AggregateMetaDataInterface,
    currentDomainModelMetadata: DomainModelMetaDataInterface,
    currentDomainModel: ModelInterface,
    processedDomainModels = []
  ) {
    if (processedDomainModels.find((element) => element === currentDomainModel) === undefined) {
      processedDomainModels.push(currentDomainModel);
      currentDomainModel.isMock = true;
      for (const relation of currentDomainModelMetadata.internalRelations) {
        const subDomainModel = currentDomainModel.getPropertyValue(
          MetaDataUtils.toCamelCase(relation.principalPropertyName)) as ModelInterface;
        if (subDomainModel != null) {
          const subCurrentEntityMetadata = metadata.domainModels
            .find((el: DomainModelMetaDataInterface) => el?.name === relation?.dependentMetaData?.name);
          // if (relation.multiplicity === PrincipalToDependentMultiplicity.OneToMany ||
          //     relation.multiplicity === PrincipalToDependentMultiplicity.ManyToMany) {
          //     const entityCollection = (subEntity as any) as EntityCollectionInterface;
          //     for (const item of entityCollection.collectionItems) {
          //         item.isMock = true;
          //         this.recursiveMockEntity(metadata, subCurrentEntityMetadata, item, processedDomainModels);
          //     }
          // } else {
          subDomainModel.isMock = true;
          this.recursiveMockDomainModel(metadata, subCurrentEntityMetadata, subDomainModel, processedDomainModels);
          // }
        }
      }
    }
  }

  private buildGridColumnsForDomainModelMetaData(
    domainModelMetaData: DomainModelMetaDataInterface,
    aggregateMetaData: AggregateMetaDataInterface,
    processedExternals: string[],
    pathName = '',
    baseLabelPath: string = '',
    inCollection = false
  ) {

    const positionOffset = 0;

    // Per ogni collection
    for (const internalCollection of domainModelMetaData.internalCollections) {

      const excludedPropertyList = ColumnsUtils.getGridExcludedPropertyListForInternalCollection(internalCollection);

      // Recupero le colonne
      const columns = ColumnsUtils.getGridColumnsFromDomainModelMetaData(internalCollection.dependentMetaData, aggregateMetaData, null, excludedPropertyList, pathName, positionOffset);

      columns.metadataShortDescription = aggregateMetaData.useMessageResourceKey ? MessageResourceManager.Current.getMessageIfExists(
        internalCollection.descriptions.displayNameKey) : internalCollection.descriptions.displayName;

      columns.metadataDescription = aggregateMetaData.useMessageResourceKey ? MessageResourceManager.Current.getMessageIfExists(
        internalCollection.descriptions.descriptionKey) : internalCollection.descriptions.description;

      columns.push(...this.recursiveBuildGridColumns(
        internalCollection.dependentMetaData,
        aggregateMetaData,
        pathName,
        baseLabelPath,
        processedExternals
      ));

      this._gridColumns.set(internalCollection.dependentMetaData.name, columns);

    }

    // Per ogni internal relation
    for (const internalRelation of domainModelMetaData.internalRelations) {

      this.buildGridColumnsForDomainModelMetaData(
        internalRelation.dependentMetaData,
        aggregateMetaData,
        processedExternals,
        inCollection ? pathName?.length > 0 ? (pathName + internalRelation.principalPropertyName) : internalRelation.principalPropertyName : '',
        baseLabelPath
      );

    }

    // Per ogni external
    for (const external of domainModelMetaData.externals) {
      external.dependentAggregateMetaData.useMessageResourceKey = aggregateMetaData.useMessageResourceKey;
      if ((processedExternals.filter(x => x == external.dependentAggregateMetaData.rootFullName)).length < 3) {
        // Evito il loop negli external annidati dello stesso tipo
        processedExternals.push(external.dependentAggregateMetaData.rootFullName);
        this.buildGridColumnsForDomainModelMetaData(
          external.dependentAggregateMetaData.rootMetaData,
          external.dependentAggregateMetaData,
          processedExternals,
          inCollection ? pathName?.length > 0 ? (pathName + external.principalPropertyName) : external.principalPropertyName : '',
          baseLabelPath
        );
      }

    }
  }

  private static RECURSION_LIMIT_TO_AVOID_CIRCULAR_REFERENCE = 50;
  private recursiveBuildGridColumns(
    metaData: DomainModelMetaDataInterface,
    aggregateMetaData: AggregateMetaDataInterface,
    pathName: string = '',
    baseLabelPath = '',
    processedExternals: string[] = [],
  ): ColumnInfoCollection {

    const columns = new ColumnInfoCollection();

    // Internal relation
    for (const internalRelation of metaData.internalRelations) {

      const excludedPropertyList = ColumnsUtils.getGridExcludedPropertyListForInternalRelation(internalRelation);
      const displayName = aggregateMetaData.useMessageResourceKey ? MessageResourceManager.Current.getMessageIfExists(internalRelation.descriptions.displayNameKey) : internalRelation.descriptions.displayName;
      const property = MetaDataUtils.toCamelCase(internalRelation.principalPropertyName);

      columns.push(...ColumnsUtils.getGridColumnsFromDomainModelMetaData(
        internalRelation.dependentMetaData,
        aggregateMetaData,
        pathName?.length > 0 ? pathName + '.' + property : property,
        excludedPropertyList,
        baseLabelPath.length === 0 ? displayName : baseLabelPath + '->' + displayName
      ));
      columns.push(...this.recursiveBuildGridColumns(
        internalRelation.dependentMetaData,
        aggregateMetaData,
        pathName?.length > 0 ? pathName + '.' + property : property,
        baseLabelPath.length === 0 ? displayName : baseLabelPath + '->' + displayName,
      ));

    }

    // External
    for (const externalRelation of metaData.externals) {

      if ((processedExternals.filter(x => x == externalRelation.dependentAggregateMetaData.rootFullName)).length < CoreOrchestratorViewModel.RECURSION_LIMIT_TO_AVOID_CIRCULAR_REFERENCE) {

        // Evito il loop negli external annidati dello stesso tipo
        processedExternals.push(externalRelation.dependentAggregateMetaData.rootFullName);

        const excludedPropertyList = ColumnsUtils.getGridExcludedPropertyListForExternalRelation(externalRelation);

        // Per sicurezza allineo useMessageResourceKey nel dependentAggregateMetaData
        externalRelation.dependentAggregateMetaData.useMessageResourceKey = aggregateMetaData.useMessageResourceKey;

        const displayName = externalRelation.dependentAggregateMetaData.useMessageResourceKey ? MessageResourceManager.Current.getMessageIfExists(externalRelation.descriptions.displayNameKey) : externalRelation.descriptions.displayName;
        const property = MetaDataUtils.toCamelCase(externalRelation.principalPropertyName);

        columns.push(...ColumnsUtils.getGridColumnsFromExternal(
          externalRelation,
          externalRelation.dependentAggregateMetaData,
          pathName?.length > 0 ? pathName + '.' + property : property,
          excludedPropertyList,
          baseLabelPath.length === 0 ? displayName : baseLabelPath + '->' + displayName,
          this.layoutMetaData != null // Sono in un layout custom?
        ));
        columns.push(...this.recursiveBuildGridColumns(
          externalRelation.dependentAggregateMetaData.rootMetaData,
          externalRelation.dependentAggregateMetaData,
          pathName?.length > 0 ? pathName + '.' + property : property,
          baseLabelPath.length === 0 ? displayName : baseLabelPath + '->' + displayName,
          processedExternals
        ));

      }
    }

    // Internal collection
    if (metaData.internalCollections.length > 0) {

      this.buildGridColumnsForDomainModelMetaData(
        metaData,
        aggregateMetaData,
        processedExternals,
        pathName,
        baseLabelPath,
        true
      );
    }

    return columns;
  }

  private async beginReportPolling({
    spoolControllerAddress,
    printOutInfoDto,
    reportLabel,
    onProgress = undefined,
    onStop = undefined,
    pollingIntervalMs = 5000
  }: {
    spoolControllerAddress: string,
    printOutInfoDto: PrintOutInfoDtoInterface,
    reportLabel: string,
    onProgress?: (data: { operationState: OperationState, attachments: AttachmentIdentityInterface[], processResult: OperationStateResultDtoInterface; }) => Promise<void>,
    onStop?: () => Promise<void>,
    pollingIntervalMs?: number;
  }): Promise<SpoolProcess<OperationStateResultDtoInterface>> {

    const operationIdentity = new OperationIdentity();
    operationIdentity.id = printOutInfoDto.id;

    const process = new SpoolProcess<OperationStateResultDtoInterface>();
    process.operationIdentity = operationIdentity;
    process.printerName = printOutInfoDto?.printerName;
    process.printerServiceAgentAddress = printOutInfoDto?.printerServiceAgentAddress;
    process.spoolControllerAddress = spoolControllerAddress;
    process.downloadAttachements = true;
    process.CheckProcess$ = this.apiClient.getOperationState(spoolControllerAddress, printOutInfoDto).pipe(
      map((data) => {
        return {
          operationState: data.result.currentOperationState,
          processResult: data.result,
          attachments: data.result.attachmentIdentities
        };
      }),
      tap(
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        async (data) => {
          if (onProgress) {
            await onProgress(data);
          }

          if (data.operationState === OperationState.EndedWithError || data.operationState === OperationState.EndedWithSuccess || data.operationState == OperationState.Stopped) {
            if (onStop) {
              await onStop();
            }
          }

          if (data.operationState === OperationState.EndedWithError) {
            this.toastMessageService.showToast({
              message: MessageResourceManager.Current.getMessageWithStrings('std_GenerateReportEndedWithError', reportLabel),
              title: MessageResourceManager.Current.getMessage(MessageCodes.Warning),
              type: ToastMessageType.warn
            });
          }

          if (data.operationState === OperationState.Stopped) {
            this.toastMessageService.showToast({
              message: MessageResourceManager.Current.getMessageWithStrings('std_GenerateReportStopped', reportLabel),
              title: MessageResourceManager.Current.getMessage(MessageCodes.Warning),
              type: ToastMessageType.warn
            });
          }

        }
      )
    );

    this.rootViewModelChangedFromNow.pipe(
      takeUntil(this.destroySubscribers$),
      take(1)
    ).subscribe(
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      async () => {
        if (onStop) {
          await onStop();
        }
      }
    );

    this.spoolProcessStarted.next(process);
    this.addSpoolProcess<OperationStateResultDto>(
      process,
      pollingIntervalMs,
    );
    return Promise.resolve(process);
  }

  protected async pingLocalPrinterService(printerServiceAgentAddress: string): Promise<boolean | string> {
    // appendo ngsw-bypass per evitare di passare dal service worker che farebbe fallire la richiesta
    const url = `${printerServiceAgentAddress?.replace(/\/$/, '') ?? ''}/api/printers?ngsw-bypass=true`;

    try {
      const response = await fetch(url, ({
        method: "GET",
        targetAddressSpace: "private",
      } as any));

      if (response.status === 401) {
        return true;
      }

      return `La stampante ha risposto con lo stato ${response.status}. Verifica la configurazione e riprova.`;
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        return false;
      }

      const errorMessage = error instanceof Error ? error.message : 'Errore sconosciuto';
      return `Impossibile contattare il servizio locale (${errorMessage}).`;
    }
  }

  async checkLocalNetworkAccess(url: string): Promise<boolean> {
    if (NetworkUtils.isLoopback(url)) {
      return true;
    }

    if (!window.location.protocol.startsWith('https:')) {
      console.warn('HTTPS richiesto per Local Network Access da IP pubblico');
      return false;
    }

    // Chrome desktop (feature flag) espone local-network-access, gli altri no
    if ('permissions' in navigator) {
      try {
        const descriptor = { name: 'local-network-access' as PermissionName };
        const permission = await navigator.permissions.query(descriptor);
        return permission.state === 'granted';
      } catch (err) {
        // Qui finiscono Android, iOS e browser non supportati
        console.warn('Local Network Access non supportato su questo browser/platform', err);
        // fallback: prova direttamente la fetch e gestisci gli errori
        return false;
      }
    }

    // Browser senza Permissions API → fallback diretto
    return false;
  }

  private async generateReportWithCommand<TParams, TRequestDto extends PrintOutDemandDtoInterface<TParams>>(
    reportCommand: UICommandInterface<any, any, OperationIdentityInterface>,
    reportInfo: ReportInfoDto,
    paramsGetter: () => TParams,
    spoolControllerAddress: string,
    dtoType: ClassConstructor<TRequestDto>,
    pollingIntervalMs: number = 5000
  ) {

    if (await firstValueFrom(reportCommand.loading$) == true) {
      await this.openSpoolProcess(reportCommand.data);
      return;
    }

    const dto = new dtoType();
    dto.printOutData = paramsGetter();
    dto.reportFileSelection.customReportItemIdentity = reportInfo.customReportItemIdentity;
    dto.reportFileSelection.standardReportIdentity = reportInfo.standardReportIdentity;

    reportCommand.loading$.next(true);

    const response = await firstValueFrom(this.apiClient.printOutFileDemand<TParams, TRequestDto>(spoolControllerAddress, dto));

    if (response.operationSuccedeed) {

      if (response.result.isDirectPrint && response.result.printerServiceAgentAddress) {
        if (!await this.checkLocalNetworkAccess(response.result.printerServiceAgentAddress)) {
          const askPermissionCommand = CommandFactory.createResultUICommand<string | boolean>(
            () => {
              return this.pingLocalPrinterService(response.result.printerServiceAgentAddress);
            },
          );

          askPermissionCommand.tooltip = 'Chiedi accesso alle impostazioni del browser per la rete locale';
          askPermissionCommand.displayName = 'Chiedi accesso';
          askPermissionCommand.iconClass = 'settings';

          const commands$ = new BehaviorSubject<{
            command: UICommandInterface,
            type: FilledButtonType;
          }[]>([
            { command: askPermissionCommand, type: FilledButtonType.Info }
          ]);

          this.toastMessageService.showToastWithCommand({
            message: 'Per poter stampare, devi consentire l\'accesso alla rete locale dal browser.',
            type: ToastMessageType.warn,
            data: {
              commands$
            }
          }).pipe(
            catchError(() => EMPTY),
            take(1),
            tap((toastResult: ToastResultInterface) => {
              if (toastResult.result === true) {
                this.handleDirectPrintResponse(
                  response, spoolControllerAddress, reportCommand
                );
                return;
              }
              reportCommand.loading$.next(false);
              this.spoolProcessError.next(null);
              if (!toastResult.cancelled) {

                if (toastResult.result === false) {
                  this.toastMessageService.showToast({
                    message: 'Il browser ha bloccato la richiesta verso la rete locale. Consenti l’accesso alla rete locale e riprova.',
                    type: ToastMessageType.warn
                  });
                } else if (typeof (toastResult.result) === 'string') {
                  this.toastMessageService.showToast({
                    message: toastResult.result,
                    type: ToastMessageType.error
                  });
                }
              }
            })
          ).subscribe();

          return;
        }
      }

      this.showFromResponse(response, MsgClearMode.NoClear);

      const onProgress = async (data: { operationState: OperationState, attachments: AttachmentIdentity[], processResult: OperationStateResultDto; }) => {
        reportCommand.loading$.next(true);
        this.moreOptionsMenuItemUpdated.next();
        this.mobileMenuItemUpdated.next();
      };

      const onStop = async () => {
        reportCommand.loading$.next(false);
        this.moreOptionsMenuItemUpdated.next();
        this.mobileMenuItemUpdated.next();
      };

      reportCommand.data = response.result;
      reportCommand.loading$.next(true);
      this.moreOptionsMenuItemUpdated.next();
      this.mobileMenuItemUpdated.next();

      await this.handlePrintOutInfoDtoResponse({
        response,
        spoolControllerAddress,
        reportCommand,
        onProgress,
        onStop,
        pollingIntervalMs
      });

    } else {
      this.showFromResponse(response, MsgClearMode.ClearAllMessages);
      this.spoolProcessError.next(null);
    }
  }

  private handleDirectPrintResponse(
    response: GenericServiceResponseInterface<PrintOutInfoDtoInterface>,
    spoolControllerAddress: string,
    reportCommand?: UICommandInterface<any, any, OperationIdentityInterface>
  ) {
    if (response.result.fileName && response.result.dataToPrint) {
      const process = new SpoolProcess<OperationStateResultDtoInterface>();
      const opertationIdentity = new OperationIdentity();
      opertationIdentity.id = response.result.id;
      process.operationIdentity = opertationIdentity;
      process.printerName = response.result?.printerName;
      process.printerServiceAgentAddress = response.result?.printerServiceAgentAddress;
      process.spoolControllerAddress = spoolControllerAddress;
      process.dataToPrint = response.result.dataToPrint;
      process.fileName = response.result.fileName;
      reportCommand.loading$.next(false);
      this.spoolProcessCompleted.next(process);
    }
  }

  private async handlePrintOutInfoDtoResponse({
    response,
    spoolControllerAddress,
    reportCommand,
    onProgress,
    onStop,
    pollingIntervalMs = 5000
  }: HandlePrintOutInfoDtoResponseParams): Promise<void> {
    if (response.result.fileName && response.result.dataToPrint) {
      this.handleDirectPrintResponse(response, spoolControllerAddress, reportCommand);
    } else {
      await this.beginReportPolling({
        spoolControllerAddress,
        printOutInfoDto: response.result,
        reportLabel: reportCommand.displayName,
        onProgress,
        onStop,
        pollingIntervalMs
      });
    }
  }
}
